import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { FiMaximize, FiLock } from 'react-icons/fi';
import {
  ReactFlow,
  addEdge as reactFlowAddEdge,
  Connection,
  Edge,
  Node,
  ReactFlowInstance,
  Background,
  Controls,
  ControlButton,
  MiniMap,
  NodeChange,
  EdgeChange,
  applyNodeChanges,
  applyEdgeChanges,
  MarkerType,
  CoordinateExtent,
  Viewport,
  NodeTypes,
  EdgeTypes,
  XYPosition
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './ReactFlowFix.css';
import { useDispatch, useSelector } from 'react-redux';
import { nodeTypes, hoverAwareNodeTypes } from '../nodes';
import { edgeTypes } from '../edges';
import { saveWorkflow, loadWorkflow } from '../../../utils/workflow';
import { 
  addNode, 
  addEdge,
  updateNode,
  updateEdge,
  removeNode,
  removeEdge,
  reconnectEdge,
  setSelectedNode,
  updateNodePosition,
  clearCenterNode,
  setHoveredNode,
  setHoveredEdge,
  setNodes,
  updateNodeData,
  addFragment,
  replaceCanvas,
} from '../../../store/workflow/workflowSlice';
import { WorkflowNode, WorkflowEdge, NodeData, RootState } from '../../../store/workflow/types';
import { sanitizeNode, sanitizeEdge, isGroupNodeType } from '../utils';
import { copySelection, cloneForPaste, hasClipboard } from '../utils/canvasClipboard';
import appStore from '../../../store';
import { findFreePosition, nodeSize, branchChildPositions, BRANCH_COL_GAP } from '../utils/nodeLayout';
import BranchAddDialog, { BranchAddResult, MERGE_NONE, MERGE_NEW } from './BranchAddDialog';
import { INSERTABLE_NODE_TYPES, categoryLabelOf, insertBlockedReason, typeLabelOf, isOnHoldType } from '../nodeCatalog';
import { isErrorEdge, ERROR_HANDLE_ID, errorTargetOffset } from '../utils/errorFlow';
import i18n from '../../../i18n';
import { useTranslation } from 'react-i18next';
import { nodeFromDataDefinition } from '../utils/dataDefinitionNode';
import { useAutoLayout } from '../hooks/useAutoLayout';
import styles from '../nodes/WorkflowNode.module.css';
import { TbTrash } from 'react-icons/tb';
import DfdCheckBar from './DfdCheckBar';
import SelectionToolbar from './SelectionToolbar';
import { GHOST_CENTERED, NODE_W, NODE_H } from '../utils/dragGhost';
import { planMembership, groupAt, descendantsOf, parentsFirst, type Rect as AbsRect } from '../utils/groupMembership';
import { checkDfd } from '../utils/dfdRules';
import { evaluateConnection } from '../utils/flowRules';
import { choiceOf, loadChoices } from '../utils/flowRuleSettings';
import { showAlert, showConfirm } from '../../common/AppDialog';
import { showToast } from '../../common/Toast';
import { updateWorkflowData } from '../../../store/project/projectSlice';
import { v4 as uuidv4 } from 'uuid';
import { createPortal } from 'react-dom';
import NodeRunMenu, { NodeRunMenuState } from './NodeRunMenu';

export const getId = () => `node_${uuidv4()}`;

// 노드 타입에 따른 기본 데이터 정의
// 새 노드의 기본 이름 — 만드는 사람의 언어로 (저장된 뒤에는 데이터라 바뀌지 않는다)
export const getDefaultNodeData = (type: string) => {
  switch (type) {
    case 'rdbData':
      return {
        label: typeLabelOf(type),
        config: {
          connection: '',
          table: '',
          mode: 'READ',
          writeMode: 'INSERT',
          columns: []
        },
      };
    case 'csvData':
      return {
        label: typeLabelOf(type),
        config: {
          filepath: '',
          delimiter: ',',
          mode: 'READ',
          hasHeader: true,
          encoding: 'UTF-8'
        },
      };
    case 'filter':
      return {
        label: typeLabelOf(type),
        config: {
          condition: ''
        },
      };
    case 'join':
      return {
        label: typeLabelOf(type),
        config: {
          joinType: 'INNER',
          condition: ''
        },
      };
    case 'branch':
      return {
        label: typeLabelOf(type),
        config: {
          condition: '',
          defaultPath: 'TRUE'
        },
      };
    case 'merge':
      return {
        label: typeLabelOf(type),
        config: {
          joinType: 'ALL'
        },
      };
    case 'userConfirm':
      return {
        label: typeLabelOf(type),
        config: {
          message: '',
          timeoutSecs: 86400,
          continueOnTimeout: false
        },
      };
    case 'memo':
      return { label: typeLabelOf(type), config: { text: '' } };
    case 'transformLoad':
      return { label: typeLabelOf(type), config: { transformMode: 'MOVE', mapping: [], mode: 'WRITE', useBulkLoader: false } };
    case 'runWorkflow':
      return { label: typeLabelOf(type), config: { workflowId: '', params: [], wait: true, timeoutSecs: 3600 } };
    case 'shellCommand':
      return { label: typeLabelOf(type), config: { command: '', params: [], cwd: '', timeoutSecs: 3600 } };
    case 'sqlScript':
      return { label: typeLabelOf(type), config: { connection: '', sql: '', params: [], fetchResult: true } };
    case 'aggregate':
      return { label: typeLabelOf(type), config: { groupBy: [], aggregations: [{ func: 'count', column: '', alias: 'cnt' }] } };
    case 'sort':
      return { label: typeLabelOf(type), config: { sortKeys: [], distinct: false, distinctKeys: [], keep: 'first' } };
    case 'lookup':
      return { label: typeLabelOf(type), config: { reference: { kind: 'db', connection: '', table: '' }, keys: [], columns: [], unmatched: 'keep', prefix: '' } };
    case 'quality':
      return { label: typeLabelOf(type), config: { rules: [], onViolation: 'reject', maxViolationPct: '' } };
    case 'compare':
      return { label: typeLabelOf(type), config: { keys: [], rules: [], sourceFilter: '', targetFilter: '', timing: 'ready', compareMode: 'memory', stageConnection: '', maxDetail: 10000 } };
    default:
      return { label: i18n.t('workflow:canvas.defaultNodeName', { type }) };
  }
};

// 그룹 노드 디버깅 로그 헬퍼 함수 개선
export const logNodeInfo = (prefix: string, node: any) => {
  console.log(`[WorkflowCanvas] ${prefix}:`, {
    id: node.id,
    type: node.type,
    position: node.position,
    parentId: node.parentId,
    style: node.style,
    width: node.width,
    height: node.height,
    extent: node.extent,
    expandParent: node.expandParent
  });
};

// 노드 영역 계산 헬퍼 함수 추가
const calculateNodeBounds = (node: any) => {
  return {
    left: node.position.x,
    top: node.position.y,
    right: node.position.x + (node.width || 150),
    bottom: node.position.y + (node.height || 50),
    width: node.width || 150,
    height: node.height || 50,
  };
};

// 콘텐츠 영역 계산 헬퍼 함수 추가
const calculateContentArea = (node: any) => {
  const bounds = calculateNodeBounds(node);
  // 타입에 따른 패딩 값 차등 적용
  let padding = { top: 40, right: 20, bottom: 20, left: 20 };
  
  if (node.type === 'subflow') {
    padding = { top: 45, right: 20, bottom: 20, left: 20 };
  } else if (node.type === 'repeatGroup') {
    padding = { top: 38, right: 20, bottom: 18, left: 20 };
  }
  
  return {
    left: bounds.left + padding.left,
    top: bounds.top + padding.top,
    right: bounds.right - padding.right,
    bottom: bounds.bottom - padding.bottom,
    width: bounds.width - padding.left - padding.right,
    height: bounds.height - padding.top - padding.bottom,
  };
};

// 기본 nodeTypes와 edgeTypes를 컴포넌트 외부로 이동
const defaultNodeTypes = nodeTypes;
const defaultEdgeTypes = edgeTypes;

// createCustomNodeTypes를 컴포넌트 외부로 이동
const createCustomNodeTypes = (originalNodeTypes: any, hoveredNodeId: string | null) => {
  return Object.fromEntries(
    Object.entries(originalNodeTypes).map(([key, Comp]) => [
      key,
      React.memo((props: any) => {
        const isHighlighted = hoveredNodeId && props.id === hoveredNodeId;
        const nodeClassName = isHighlighted
          ? `${props.className || ''} ${styles.highlight}`.trim()
          : props.className || '';
        
        return React.createElement(Comp as any, {
          ...props,
          className: nodeClassName,
          isHovered: isHighlighted,
          data: {
            ...props.data,
            isHighlighted
          }
        });
      })
    ])
  );
};

const calculateChildPosition = (
  parentNode: WorkflowNode,
  dropPosition: { x: number, y: number },
  reactFlowBounds: DOMRect,
  viewport: Viewport
): { x: number, y: number } => {
  // 부모 노드의 컨텐츠 영역 기준으로 상대 위치 계산
  const parentElement = document.querySelector(`[data-id="${parentNode.id}"]`);
  const parentContent = parentElement?.querySelector('.group-content') as HTMLElement;
  
  if (!parentContent) {
    console.warn('부모 노드의 컨텐츠 영역을 찾을 수 없습니다.');
    return { x: 0, y: 0 };
  }
  
  const parentRect = parentContent.getBoundingClientRect();
  const x = ((dropPosition.x - parentRect.left) / viewport.zoom);
  const y = ((dropPosition.y - parentRect.top) / viewport.zoom);
  
  // 패딩 고려 (group-content의 padding이 10px)
  return {
    x: Math.max(10, Math.min(x, parentNode.width ? parentNode.width - 160 : x)),
    y: Math.max(10, Math.min(y, parentNode.height ? parentNode.height - 90 : y))
  };
};

// 컴포넌트 외부에 memoized node types 정의
const MemoizedNodeTypes = Object.fromEntries(
  Object.entries(nodeTypes).map(([key, Comp]) => [
    key,
    React.memo((props: any) => React.createElement(Comp as any, props))
  ])
);

// 그룹 노드 크기 상수 정의
const GROUP_NODE_DIMENSIONS = {
  subflow: { width: 640, height: 480 },
  group: { width: 600, height: 480 },
  repeatGroup: { width: 560, height: 440 }
};

interface WorkflowCanvasProps {
  /** 캔버스(React Flow 영역) 위에 띄울 것 — 다운로드 버튼 같은 오버레이. 상단 검사 바·의존성 바와 겹치지 않도록 여기로 받는다 */
  overlay?: React.ReactNode;
}

const WorkflowCanvas: React.FC<WorkflowCanvasProps> = ({ overlay }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // 선택 영역 복사/붙여넣기 — Ctrl+C(복사) · Ctrl+V(붙여넣기) · Ctrl+D(즉시 복제).
  // 입력란 포커스 중에는 브라우저 기본 동작(텍스트 복사)을 살린다. 최신 상태는 스토어에서 직접 읽는다(스테일 클로저 방지).
  /** 마지막으로 누른 곳이 캔버스인가 — Ctrl+C 가 노드 복사인지 글자 복사인지 가르는 기준 */
  const lastPointerInCanvas = useRef(false);
  useEffect(() => {
    const onDown = (e: PointerEvent) => { lastPointerInCanvas.current = !!(e.target as HTMLElement | null)?.closest?.('.react-flow'); };
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      const k = e.key.toLowerCase();
      if (k !== 'c' && k !== 'v' && k !== 'd') return;
      const w = (appStore.getState() as any).workflow;
      if (!w.current?.id) return;
      if (k === 'c' || k === 'd') {
        // 글자 복사를 우선하는 것은 **마지막으로 캔버스 밖을 눌렀을 때**(속성 패널·로그의 글자를 고른 경우)뿐이다.
        // 예전에는 화면 어디든 글자 선택이 남아 있으면 '글자 복사' 로 보고 노드 복사를 건너뛰어, 클립보드에
        // **이전 복사본이 남았다**(현장 2026-09-28: 영역을 골라 복사·붙여넣기 하면 옛것이 나온다). 탐색기 등에서
        // 한 번 끌어 고른 글자는 캔버스를 눌러도 선택이 그대로 남기 때문이다.
        if (k === 'c') {
          const sel = window.getSelection?.();
          const hasText = !!sel && String(sel).length > 0;
          if (hasText && !lastPointerInCanvas.current) return;
          if (hasText) sel!.removeAllRanges();          // 노드를 복사한다 — 남아 있던 글자 선택은 걷는다
        }
        const copied = copySelection(w.nodes, w.edges);
        if (!copied) return;
        if (k === 'c') return;                      // 복사만
        e.preventDefault();                          // Ctrl+D: 브라우저 즐겨찾기 방지
      }
      if (k === 'v' && !hasClipboard()) return;
      if (k === 'v') e.preventDefault();
      const frag = cloneForPaste(w.nodes);
      if (frag) dispatch({ type: 'workflow/addFragment', payload: frag });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // 노드 드래그 시작 시 자식 노드들의 상대 위치를 저장하는 ref
  
  // useSelector 타입 명시적 지정
  const storeNodes = useSelector<RootState, WorkflowNode[]>(state => state.workflow.nodes);
  const storeEdges = useSelector<RootState, WorkflowEdge[]>(state => state.workflow.edges);
  const wfSettings = useSelector((state: any) => state.workflow.settings);
  const selectedNodeId = useSelector<RootState, string | null>(state => state.workflow.selectedNodeId);
  const centerNodeId = useSelector<RootState, string | null>(state => state.workflow.centerNodeId);
  const fitViewRequestId = useSelector<RootState, number>(state => state.workflow.fitViewRequestId);
  const currentWorkflow = useSelector<RootState, any>(state => state.workflow.current);
  /** 고른 노드들 — 2개 이상이면 선택 도구 막대(삭제·이미지 복사·정렬)를 띄운다 */
  const selectedIds = useMemo(() => storeNodes.filter((n: any) => n.selected).map((n: any) => n.id as string), [storeNodes]);
  /** 범위 끌기가 끝났을 때 고른 노드들 — 범위로 1개만 골라도 이미지 복사는 보인다(클릭으로 고른 1개는 아님).
   *  지금 선택이 그것과 같을 때만 '범위 선택' 으로 본다 — 다른 노드를 누르거나 빈 곳을 누르면 저절로 아니게 된다 */
  const [boxIds, setBoxIds] = useState<string[]>([]);
  const boxSelected = boxIds.length > 0 && boxIds.length === selectedIds.length && boxIds.every((id) => selectedIds.includes(id));
  // 워터마크용 프로젝트명 — 프로젝트 스토어는 RootState(workflow 전용 타입)에 없어 any 로 읽는다
  const projectName = useSelector((state: any) => {
    const p = state.project;
    return p?.projects?.find((x: any) => x.id === p.selectedProjectId)?.name || '';
  });
  const hoveredNodeId = useSelector<RootState, string | null>(state => state.workflow.hoveredNodeId);
  const hoveredEdgeId = useSelector<RootState, string | null>(state => state.workflow.hoveredEdgeId);
  // 고른 연결선 — 화면만의 상태라 스토어(저장·되돌리기 대상)에 넣지 않는다. 예전에는 React Flow 의 선택 변경을
  // 버려서 연결선을 고를 수도, 골라서 Delete 로 지울 수도 없었다(2026-10-20 현장)
  const [selectedEdgeIds, setSelectedEdgeIds] = useState<Set<string>>(() => new Set());

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance<WorkflowNode, WorkflowEdge> | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dimensions, setDimensions] = useState({ width: '100%', height: '100vh' });

  // 메모이제이션된 노드 타입과 엣지 타입
  const memoizedNodeTypes = hoverAwareNodeTypes; // 고정 참조 — 호버는 래퍼가 스토어에서 읽는다
  const memoizedEdgeTypes = useMemo(() => edgeTypes, []);

  const defaultEdgeOptions = {
    animated: false, // 애니메이션 비활성화
    style: { strokeWidth: 2, stroke: 'var(--edge-stroke)' },
    zIndex: 10, // Ensure edges are rendered on top of nodes
    markerEnd: 'url(#react-flow__arrowclosed)',
  };
  
  const nodePositions = useRef<Record<string, {x: number, y: number}>>({});

  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [replaceTargetNode, setReplaceTargetNode] = useState<WorkflowNode | null>(null);
  const [replaceNewType, setReplaceNewType] = useState<string | null>(null);
  const [replaceNewData, setReplaceNewData] = useState<any>(null);

  // 배율 자동 조정 모드 — 기본 자동. 노드 수가 바뀌면 fitView 로 전체가 보이게 맞춘다.
  // 사용자가 휠/핀치/± 버튼으로 배율을 직접 바꾸면 수동으로 전환되고, 토글로 다시 자동으로 돌리면 즉시 재맞춤.
  const [autoZoom, setAutoZoom] = useState(true);
  const autoZoomRef = useRef(true);
  useEffect(() => { autoZoomRef.current = autoZoom; }, [autoZoom]);
  const lastZoomRef = useRef<number | null>(null);
  // 노드 수에 비례해 배율 상한을 낮춘다: 1개 1.25 → 5개 ≈0.93 → 10개 ≈0.55 → 이후 0.4 하한.
  // fitView 만으로는 노드가 화면 안에 들어가는 동안 상한(1.5)에 머물러 "개수에 따라 줄어드는" 느낌이 없었다.
  const autoFitOptionsFor = useCallback((count: number) => ({
    padding: 0.2,
    duration: 250,
    minZoom: 0.4,
    maxZoom: Math.max(0.4, Math.min(1.25, 1.25 - 0.08 * (count - 1))),
  }), []);

  const storeNodesLenRef = useRef(0);
  useEffect(() => { storeNodesLenRef.current = storeNodes.length; }, [storeNodes.length]);

  const [dragging, setDragging] = useState(false);
  const insertNodeOfTypeRef = useRef<((edgeId: string, type: string, at?: { x: number; y: number }) => void) | null>(null);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    // 표준 경로: applyNodeChanges 로 position(매 프레임)·dimensions(최초 측정)·select 를 모두 반영해
    // 스토어에 통째로 저장한다. nodes={storeNodes} 제어 모드에서는 이 변경을 하나라도 버리면
    // React Flow 가 옛 위치로 되돌려 노드가 튀고, dimensions 를 버리면 "not initialized" 경고가 난다.
    // (이전 구현은 150ms 스로틀로 변경을 폐기했고 dimensions 를 무시했음)
    const removes = changes.filter((c): c is Extract<NodeChange, { type: 'remove' }> => c.type === 'remove');
    const rest = changes.filter(c => c.type !== 'remove');
    if (rest.length) {
      // applyNodeChanges 는 dimensions 변경(리사이저 등)에서 node.style.width 를 제자리에서 대입한다.
      // 스토어 노드는 Immer 가 동결(freeze)한 객체라 그대로 넘기면 "Cannot assign to read only property" 가 나므로
      // style/measured 를 얕은 복사한 사본을 넘긴다.
      // ★ 렌더 클로저의 storeNodes 가 아니라 스토어에서 "지금" 상태를 읽는다 — 언두/리두로 노드가
      //   프로그램적으로 바뀐 직후 React Flow 가 옛 스냅샷 기준의 변경(select 등)을 되쏘는데,
      //   스테일 배열에 적용하면 방금 되돌린 캔버스를 다시 덮어써 언두가 무효가 된다.
      const freshNodes = (appStore.getState() as any).workflow.nodes as Node[];
      const alive = new Set(freshNodes.map(n => n.id));
      const applicable = rest.filter((c: any) => !('id' in c) || alive.has(c.id));
      if (applicable.length) {
        const thawed = freshNodes.map(n => ({
          ...n,
          style: n.style ? { ...n.style } : n.style,
          measured: n.measured ? { ...n.measured } : n.measured,
        }));
        dispatch(setNodes(applyNodeChanges(applicable, thawed)));
      }
    }
    const aliveNow = new Set(((appStore.getState() as any).workflow.nodes as Node[]).map(n => n.id));
    // 선택 변경은 **묶음으로** 판단한다. 다른 노드를 클릭하면 한 묶음에 [새 노드 selected:true, 이전 노드 selected:false]
    // 가 함께 오는데, 이것을 순서대로 하나씩 반영하면 뒤에 오는 '해제'가 방금 고른 노드까지 지워 버린다
    // — 노드를 눌러도 속성이 안 뜨고 다시 눌러야 뜨던 까닭이다(현장 2026-09-23).
    const selects = changes.filter((c): c is Extract<NodeChange, { type: 'select' }> => c.type === 'select');
    if (selects.length) {
      const picked = [...selects].reverse().find(c => c.selected && aliveNow.has(c.id));
      if (picked) dispatch(setSelectedNode(picked.id));
      else {
        // 아무것도 고르지 않은 묶음 — 지금 고른 것이 풀린 것일 때만 비운다
        const cur = (appStore.getState() as any).workflow.selectedNodeId as string | null;
        if (cur && selects.some(c => !c.selected && c.id === cur)) dispatch(setSelectedNode(null));
      }
    }
    // remove 는 연결 엣지 정리까지 하는 기존 리듀서 사용
    removes.forEach(c => dispatch(removeNode(c.id)));
  }, [dispatch, storeNodes]);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    const selects = changes.filter((c): c is EdgeChange & { type: 'select'; id: string; selected: boolean } => c.type === 'select');
    if (selects.length) {
      setSelectedEdgeIds((prev) => {
        const next = new Set(prev);
        selects.forEach((c) => (c.selected ? next.add(c.id) : next.delete(c.id)));
        return next;
      });
    }
    changes.forEach(change => {
      if (change.type === 'remove') {
        dispatch(removeEdge(change.id));
        setSelectedEdgeIds((prev) => { if (!prev.has(change.id)) return prev; const next = new Set(prev); next.delete(change.id); return next; });
      }
    });
  }, [dispatch]);
  
  // 연결선에 덮인 노드도 고르고 끌 수 있게 — 연결선 층이 노드 층보다 위라서(index.css), 선이 여러 개 지나가는 노드는
  // 마우스가 선에 먼저 걸려 선택도 이동도 되지 않았다(현장 2026-10-12, 선 16개가 지나가는 파티션 노드).
  // 마우스 아래에 (그룹이 아닌) 노드가 있으면 그동안 연결선이 클릭을 가로채지 않게 한다(.of-node-under) — 노드가 먼저다.
  // 노드 밖의 선은 그대로 눌린다. 끄는 중(버튼 눌림)에는 건드리지 않는다.
  useEffect(() => {
    const el = reactFlowWrapper.current;
    if (!el) return;
    let raf = 0;
    const isNode = (x: Element) => x.classList.contains('react-flow__node')
      && !/react-flow__node-(group|subflow|repeatGroup)/.test(x.className);
    const onMove = (e: MouseEvent) => {
      if (e.buttons || raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        // elementsFromPoint 는 pointer-events:none 인 것을 건너뛴다 — 선을 꺼 둔 동안에도 아래 노드가 그대로 잡힌다
        const under = document.elementsFromPoint(e.clientX, e.clientY)
          .some((x) => { const n = x.closest('.react-flow__node'); return !!n && isNode(n); });
        el.classList.toggle('of-node-under', under);
      });
    };
    const onLeave = () => el.classList.remove('of-node-under');
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => { el.removeEventListener('mousemove', onMove); el.removeEventListener('mouseleave', onLeave); if (raf) cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => {
    function updateDimensions() {
      const parentHeight = document.documentElement.clientHeight;
      const availableHeight = parentHeight - 60; // 헤더 높이 60px 고려
      setDimensions({
        width: '100%',
        height: `${availableHeight}px`
      });
      
      // React Flow 컨테이너 레이아웃 강제 조정
      setTimeout(() => {
        const rfContainer = document.querySelector('.react-flow__container, .xyflow__container');
        const rfPane = document.querySelector('.react-flow__pane, .xyflow__pane');
        
        if (rfContainer && rfPane) {
          // 컨테이너가 전체 영역을 차지하도록 직접 DOM 조작
          (rfContainer as HTMLElement).style.width = '100%';
          (rfContainer as HTMLElement).style.height = '100%';
          (rfContainer as HTMLElement).style.position = 'absolute';
          (rfContainer as HTMLElement).style.top = '0';
          (rfContainer as HTMLElement).style.left = '0';
          
          // 패인도 동일하게 조정
          (rfPane as HTMLElement).style.width = '100%';
          (rfPane as HTMLElement).style.height = '100%';
          (rfPane as HTMLElement).style.position = 'absolute';
          (rfPane as HTMLElement).style.top = '0';
          (rfPane as HTMLElement).style.left = '0';
          
          console.log('[WorkflowCanvas] React Flow 컨테이너 레이아웃 강제 조정 완료');
        }
      }, 300);
    }

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (reactFlowInstance && !autoZoomRef.current) { // 자동 모드에서는 아래 autoZoom 효과가 배율을 맡는다
      setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.2 });
        reactFlowInstance.zoomTo(1.0);
        reactFlowInstance.setCenter(0, 0);
      }, 300);
    }
  }, [reactFlowInstance]);

  // 노드 수(추가/삭제/워크플로 로드)에 따라 배율 자동 조정
  useEffect(() => {
    if (!autoZoom || !reactFlowInstance) return;
    const t = setTimeout(() => {
      reactFlowInstance.fitView(autoFitOptionsFor(storeNodes.length));
    }, 80); // 새 노드의 measured 가 반영된 뒤 실행
    return () => clearTimeout(t);
  }, [storeNodes.length, autoZoom, reactFlowInstance, autoFitOptionsFor]);

  // 탐색기에서 워크플로를 (다시) 열면 처음 열었을 때와 같은 화면으로 되돌린다.
  // 위 효과는 '노드 수 변화'에만 반응하는데, 같은 워크플로를 다시 열면 reset→재주입이 한 렌더로 배칭돼
  // 노드 수도 id 도 그대로여서 아무 일도 일어나지 않았다(노드를 클릭해 확대해 둔 화면이 남음).
  // 그래서 탐색기가 명시적으로 보내는 신호(fitViewRequestId)를 쓴다.
  useEffect(() => {
    if (!fitViewRequestId || !reactFlowInstance) return;
    const t = setTimeout(() => {   // 노드가 모두 mount·measure 된 뒤
      reactFlowInstance.fitView(autoFitOptionsFor(storeNodesLenRef.current));
    }, 180);
    return () => clearTimeout(t);
    // storeNodes.length 는 의도적으로 의존성에서 뺀다 — 신호가 올 때만 한 번 맞춘다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitViewRequestId, reactFlowInstance, autoFitOptionsFor]);

  // 사용자 제스처(휠/핀치/드래그)로 배율이 바뀌면 수동 모드로. 프로그램 호출(fitView 등)은 event 가 null 이라 제외.
  // React Flow 가 onMoveEnd 에 넘기는 event 는 휠 줌에서 비어 있을 수 있어, 래퍼에서 직접 잡은 제스처 시각을 함께 본다.
  const lastGestureTsRef = useRef(0);
  const noteZoomGesture = useCallback(() => { lastGestureTsRef.current = Date.now(); }, []);
  const onMoveEnd = useCallback((event: MouseEvent | TouchEvent | null, viewport: { x: number; y: number; zoom: number }) => {
    const prev = lastZoomRef.current;
    lastZoomRef.current = viewport.zoom;
    const zoomChanged = prev !== null && Math.abs(viewport.zoom - prev) > 1e-6;
    const userGesture = !!event || Date.now() - lastGestureTsRef.current < 1500;
    if (zoomChanged && userGesture && autoZoomRef.current) {
      setAutoZoom(false);
    }
  }, []);
  const markManualZoom = useCallback(() => { if (autoZoomRef.current) setAutoZoom(false); }, []);

  const onInit = useCallback((instance: ReactFlowInstance<WorkflowNode, WorkflowEdge>) => {
    console.log('[WorkflowCanvas] ReactFlow 인스턴스 초기화됨');
    setReactFlowInstance(instance);
    
    const enforceReactFlowLayout = () => {
      // React Flow의 내부 DOM 구조 접근
      const reactFlowElement = reactFlowWrapper.current?.querySelector('.react-flow, .xyflow');
      const container = reactFlowElement?.querySelector('.react-flow__container, .xyflow__container');
      const pane = reactFlowElement?.querySelector('.react-flow__pane, .xyflow__pane');
      const viewport = reactFlowElement?.querySelector('.react-flow__viewport, .xyflow__viewport');
      
      if (!reactFlowElement || !container || !pane || !viewport) {
        console.warn('[WorkflowCanvas] React Flow DOM 요소를 찾을 수 없음');
        return;
      }
      
      // DOM 구조 문제 감지 (viewport가 container 클래스를 가진 경우)
      if (viewport.classList.contains('react-flow__container') || viewport.classList.contains('xyflow__container')) {
        console.log('[WorkflowCanvas] viewport가 container 클래스를 가지고 있음 - 수정 필요');
        
        // 1. viewport에서 container 클래스 제거 시도
        viewport.classList.remove('react-flow__container');
        viewport.classList.remove('xyflow__container');
        
        // 2. 실제 container 요소에 클래스 적용 (이미 있는 경우 무시됨)
        container.classList.add('react-flow__container');
        
        // 3. 필요한 경우 transform 스타일 조정 - 초기화는 하지 않음 (패닝/줌 유지 필요)
      }
      
      // container 요소가 전체 영역을 차지하도록 조정
      (container as HTMLElement).style.width = '100%';
      (container as HTMLElement).style.height = '100%';
      (container as HTMLElement).style.position = 'absolute';
      (container as HTMLElement).style.top = '0';
      (container as HTMLElement).style.left = '0';
      (container as HTMLElement).style.right = '0';
      (container as HTMLElement).style.bottom = '0';
      
      // pane 요소가 전체 영역을 차지하도록 조정
      (pane as HTMLElement).style.width = '100%';
      (pane as HTMLElement).style.height = '100%';
      (pane as HTMLElement).style.position = 'absolute';
      (pane as HTMLElement).style.top = '0';
      (pane as HTMLElement).style.left = '0';
      (pane as HTMLElement).style.right = '0';
      (pane as HTMLElement).style.bottom = '0';
      
      // viewport 요소의 위치 확인
      console.log('[WorkflowCanvas] React Flow 레이아웃 강제 조정 완료', {
        reactFlowRect: (reactFlowElement as HTMLElement).getBoundingClientRect(),
        containerRect: (container as HTMLElement).getBoundingClientRect(),
        paneRect: (pane as HTMLElement).getBoundingClientRect(),
        viewportRect: (viewport as HTMLElement).getBoundingClientRect(),
        viewportTransform: (viewport as HTMLElement).style.transform
      });
    };
    
    // 초기화 후 약간의 지연을 두고 레이아웃 강제 조정 및 뷰포트 설정
    setTimeout(() => {
      enforceReactFlowLayout(); // 레이아웃 먼저 조정

      // 그 다음에 뷰포트 설정 (애니메이션 시간 고려하여 순차 적용)
      instance.fitView({ padding: 0.2, duration: 200 }); // 부드럽게 fitView
      setTimeout(() => { // fitView 후 zoomTo 와 setCenter 적용 (수동 모드일 때만 — 자동 모드는 fitView 결과 유지)
        if (!autoZoomRef.current) {
          instance.zoomTo(1.0, { duration: 200 });
          instance.setCenter(0, 0, { duration: 200, zoom: 1.0 }); // zoom 옵션 추가
        }
        console.log('[WorkflowCanvas] 초기 뷰포트 설정 완료: zoom=1, center=(0,0)');
        
        // 뷰포트 설정 후 그룹 노드 로깅 (선택 사항)
        const allNodes = instance.getNodes();
        const groupNodes = allNodes.filter(node => isGroupNodeType(node.type || ''));
        console.log(`[WorkflowCanvas] 모든 그룹 노드 초기 상태 (${groupNodes.length}개):`, 
          groupNodes.map(node => ({
            id: node.id,
            type: node.type,
            position: node.position,
            width: node.width,
            height: node.height,
            style: node.style,
            domWidth: document.querySelector(`[data-id="${node.id}"]`)?.getBoundingClientRect().width,
            domHeight: document.querySelector(`[data-id="${node.id}"]`)?.getBoundingClientRect().height,
            childNodes: allNodes
              .filter(n => n.parentId === node.id)
              .map(childNode => ({
                id: childNode.id,
                type: childNode.type,
                position: childNode.position,
                width: childNode.width,
                height: childNode.height
              }))
          }))
        );
      }, 250); // fitView 애니메이션 시간 고려
    }, 100);
  }, []); // 의존성 배열 비우기 (마운트 시 한 번만 실행되도록)

  /** 노드의 절대 좌표 상자 — React Flow 가 계산해 둔 값 */
  const absRectOf = useCallback((id: string): AbsRect | null => {
    const n: any = reactFlowInstance?.getInternalNode(id);
    if (!n) return null;
    const p = n.internals?.positionAbsolute ?? n.position;
    return { x: p.x, y: p.y, w: n.measured?.width ?? n.width ?? 0, h: n.measured?.height ?? n.height ?? 0 };
  }, [reactFlowInstance]);

  /**
   * 금지 규칙 2안 — source 와 target 사이에 노드(데이터처리·내부데이터)를 넣어 잇는다.
   * 새 노드는 두 노드 사이의 빈 자리에 둔다(다른 노드를 밀지 않는다). 노드와 연결 둘을 **한 액션(addFragment)** 으로 넣어
   * 되돌리기 한 번에 사라지게 한다.
   */
  const connectVia = useCallback((sourceId: string, targetId: string, midType: string) => {
    const sourceNode = storeNodes.find(n => n.id === sourceId);
    const targetNode = storeNodes.find(n => n.id === targetId);
    if (!sourceNode || !targetNode) return;
    const size = { width: NODE_W, height: NODE_H };
    // 좌표계를 맞춘다 — 두 노드가 같은 그룹 안이면 새 노드도 그 그룹 안에(그룹 기준 좌표), 아니면 최상위에(절대 좌표).
    // 예전에는 그룹을 따지지 않아 서브플로 안 두 데이터 사이에 넣은 데이터처리가 서브플로 밖에 생겼다(현장 2026-10-01)
    const sameParent = (sourceNode.parentId || null) === (targetNode.parentId || null);
    const parentId = sameParent ? (sourceNode.parentId || null) : null;
    const at = (n: any) => {
      if (sameParent) return { x: n.position.x, y: n.position.y };
      const r = absRectOf(n.id);
      return r ? { x: r.x, y: r.y } : { x: n.position.x, y: n.position.y };
    };
    const sp = at(sourceNode);
    const tp = at(targetNode);
    const srcSize = nodeSize(sourceNode);
    const desired = tp.x > sp.x + srcSize.width + size.width
      ? { x: (sp.x + srcSize.width + tp.x) / 2 - size.width / 2, y: (sp.y + tp.y) / 2 }
      : { x: sp.x + srcSize.width + 40, y: sp.y };
    const siblings = storeNodes.filter((n: any) => (n.parentId || null) === parentId && !isGroupNodeType(n.type || ''));
    const position = findFreePosition(siblings as any, desired, size);
    const midId = getId();
    dispatch(addFragment({
      nodes: [{ id: midId, type: midType, position, data: { ...getDefaultNodeData(midType) },
        draggable: true, className: 'workflow-child-node', style: { zIndex: 10 },
        ...(parentId ? { parentId, extent: 'parent', expandParent: true } : {}) } as any],
      edges: [
        { id: `${sourceId}-${midId}`, source: sourceId, target: midId, type: 'workflow' } as any,
        { id: `${midId}-${targetId}`, source: midId, target: targetId, type: 'workflow' } as any,
      ],
    }));
  }, [storeNodes, dispatch, absRectOf]);

  /**
   * 연결 규칙 — 잇기 전에 판정한다(utils/flowRules). 금지면 막거나(1안) 사이에 노드를 넣도록 안내(2안),
   * 주의면 잇고 알리거나(1안) 잇기 전에 확인(2안). 사용자가 규칙마다 고른다(연결 규칙 설정 — DFD 검사 바).
   * 이어도 되면 true. onInsert 가 없으면(끝 옮기기) 2안의 「사이에 넣기」 대신 막는다.
   */
  const passesFlowRules = useCallback(async (connection: Connection, edges: any[],
    onInsert?: (insertType: string) => void): Promise<boolean> => {
    const verdict = evaluateConnection(connection, storeNodes as any, edges);
    const choices = loadChoices(appStore.getState().auth.user?.loginId);
    if (verdict.forbidden) {
      const { rule, params } = verdict.forbidden;
      if (onInsert && choiceOf(rule, choices) === 'opt2' && rule.insertType) {
        const ok = await showConfirm(t(`workflow:flowRule.${rule.id}.ask`, params),
          { title: t('workflow:flowRule.blockedTitle'), okText: t(`workflow:flowRule.${rule.id}.okInsert`) });
        if (ok) onInsert(rule.insertType);
        return false;
      }
      await showAlert(t(`workflow:flowRule.${rule.id}.block`, params), { title: t('workflow:flowRule.blockedTitle') });
      return false;
    }
    const notices: string[] = [];
    for (const { rule, params } of verdict.cautions) {
      const msg = t(`workflow:flowRule.${rule.id}.warn`, params);
      if (choiceOf(rule, choices) === 'opt2') {
        const ok = await showConfirm(msg, { title: t('workflow:flowRule.confirmTitle'), okText: t('workflow:flowRule.confirmOk') });
        if (!ok) return false;
      } else {
        notices.push(msg);
      }
    }
    if (notices.length) showToast(notices.join('\n'));
    return true;
  }, [storeNodes, appStore, t]);

  /**
   * 연결선 끝(화살표 쪽이든 시작 쪽이든)을 잡아 다른 노드로 옮긴다 — 지웠다 다시 잇지 않아도 된다(2026-10-20 현장 요청).
   * 규칙은 새로 잇는 것과 같고(옮기는 선 자신은 빼고 판정), 되돌리기 한 번에 원래대로. 빈 곳에 놓으면 그대로 둔다
   */
  const onReconnect = useCallback(async (oldEdge: Edge, connection: Connection) => {
    if (!connection.source || !connection.target) return;
    if (oldEdge.source === connection.source && oldEdge.target === connection.target
        && (oldEdge.sourceHandle || null) === (connection.sourceHandle || null)) return;
    const others = (storeEdges as any[]).filter((e) => e.id !== oldEdge.id);
    if (!(await passesFlowRules(connection, others))) return;
    const { data, ...rest } = oldEdge as any;
    const newId = `e${connection.source}-${connection.target}${connection.sourceHandle ? `-${connection.sourceHandle}` : ''}`;
    setSelectedEdgeIds((prev) => (prev.has(oldEdge.id) ? new Set([...Array.from(prev).filter((x) => x !== oldEdge.id), newId]) : prev));
    dispatch(reconnectEdge({ oldId: oldEdge.id, edge: {
      ...rest, data, source: connection.source, target: connection.target,
      sourceHandle: connection.sourceHandle ?? null, targetHandle: connection.targetHandle ?? null,
      id: newId,
    } as any }));
  }, [storeEdges, passesFlowRules, dispatch]);

  const onConnect = useCallback(async (connection: Connection) => {
    if (!(await passesFlowRules(connection, storeEdges as any,
      (insertType) => connectVia(connection.source, connection.target, insertType as any)))) return;

    // 새 엣지에 대한 기본 ID 생성
    const newEdge = {
      ...connection,
      id: `e${connection.source}-${connection.target}`,
      type: 'workflow'
    };
    dispatch(addEdge(newEdge));

    // 엣지 연결 시 target 노드를 source 오른쪽에 자동 정렬
    const sourceNode = storeNodes.find(n => n.id === connection.source);
    const targetNode = storeNodes.find(n => n.id === connection.target);
    // 데이터비교로 잇는 점선은 흐름이 아니다 — 두 입력을 이을 때마다 비교 노드가 끌려다니지 않게 그대로 둔다
    if (sourceNode && targetNode && targetNode.type !== 'compare') {
      const offsetX = (sourceNode.width || 200) + 60; // 적당한 간격
      // 예외 흐름(실패 시 가는 길)은 정상 흐름 **아래**로 — 같은 줄에 놓으면 정상 타깃과 겹치고,
      // 30° 로 내려가는 선이 지날 자리도 없다. 이미 무언가 있으면 가까운 빈 자리로 (utils/nodeLayout)
      const isError = (connection.sourceHandle || '') === ERROR_HANDLE_ID;
      const off = errorTargetOffset(nodeSize(sourceNode).width);
      const desired = isError
        ? { x: sourceNode.position.x + off.dx, y: sourceNode.position.y + off.dy }
        : { x: sourceNode.position.x + offsetX, y: sourceNode.position.y };
      const position = isError
        ? findFreePosition(storeNodes.filter(n => n.id !== targetNode.id), desired, nodeSize(targetNode), { excludeId: targetNode.id })
        : desired;
      dispatch(updateNodePosition({ id: targetNode.id, position }));
    }
  }, [dispatch, storeNodes, storeEdges, connectVia, passesFlowRules]);

  // 노드 오른쪽 클릭 → 부분 실행 메뉴. 여러 개 선택한 상태에서 그중 하나를 누르면 선택 전체가 대상이다
  const [runMenu, setRunMenu] = useState<NodeRunMenuState | null>(null);
  const closeRunMenu = useCallback(() => setRunMenu(null), []);
  const onNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
    if (node.type === 'memo' || node.type === 'placeholder') return;   // 주석·자리표시는 실행 대상이 아니다
    event.preventDefault();
    const selected = (reactFlowInstance?.getNodes() || [])
      .filter((n) => n.selected && n.type !== 'memo' && n.type !== 'placeholder').map((n) => n.id);
    const nodeIds = selected.length > 1 && selected.includes(node.id) ? selected : [node.id];
    setRunMenu({ x: event.clientX, y: event.clientY, nodeId: node.id, nodeIds });
  }, [reactFlowInstance]);

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setBoxIds([]);                              // 클릭으로 고른 것 — 범위 선택 도구(이미지 복사)는 2개 이상일 때만
    dispatch(setSelectedNode(node.id));
  }, [dispatch]);

  // 툴박스 드래그가 캔버스 밖(툴박스 등)에서 끝나면 onDrop 이 오지 않아 드롭 안내(원+십자)와 엣지 + 강조가 남는다 → 전역 dragend 로 정리
  useEffect(() => {
    const end = () => { setDragging(false); setDragPosition(null); };
    document.addEventListener('dragend', end);
    return () => document.removeEventListener('dragend', end);
  }, []);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    
    if (!reactFlowWrapper.current || !reactFlowInstance) return;
    
    // 드래그 위치를 ReactFlow(플로우) 좌표로 변환 — React Flow 의 공식 변환을 쓴다.
    // (예전 수식 (clientX-left)/zoom - viewport.x 는 팬 오프셋을 줌으로 나누지 않아
    //  줌이 1이 아니면 좌표가 크게 어긋나 그룹 안 드롭이 자식으로 붙지 않았다)
    const position: XYPosition = reactFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const viewport = reactFlowInstance.getViewport(); // 근접 판정 임계값(50px) 을 플로우 단위로 환산할 때 사용
    
    // 가장 가까운 노드 찾기
    let closestNode: WorkflowNode | null = null;
    let minDistance = Number.MAX_VALUE;
    
    // 타입 단언 없이 storeNodes 사용 (이미 올바른 타입으로 지정됨)
    for (const node of storeNodes) {
      if (node.type !== 'placeholder' && isGroupNodeType(node.type)) {
        const nodeWidth = node.width || 600;
        const nodeHeight = node.height || 480;
        
        // 노드의 중심점 계산
        const nodeCenterX = node.position.x + nodeWidth / 2;
        const nodeCenterY = node.position.y + nodeHeight / 2;
        
        // 마우스 포인터가 노드 영역 내에 있는지 확인
        const isInside = 
          position.x >= node.position.x &&
          position.x <= node.position.x + nodeWidth &&
          position.y >= node.position.y &&
          position.y <= node.position.y + nodeHeight;
        
        if (isInside) {
          // 노드 내부에 있으면 최소 거리로 설정
          minDistance = 0;
          closestNode = node;
          break; // 내부에 있는 노드를 찾으면 즉시 중단
        } else {
          // 노드 외부에 있으면 중심점까지의 거리 계산
          const distance = Math.sqrt(
            Math.pow(position.x - nodeCenterX, 2) +
            Math.pow(position.y - nodeCenterY, 2)
          );
          
          if (distance < minDistance) {
            minDistance = distance;
            closestNode = node;
          }
        }
      }
    }
    
    // 감지 임계값 (줌 레벨에 따라 조정)
    const detectionThreshold = 50 / viewport.zoom;
    
    // 호버 상태 업데이트
    if (minDistance <= detectionThreshold && closestNode) {
      const nodeId = closestNode.id;
      if (hoveredNodeId !== nodeId) {
        dispatch(setHoveredNode(nodeId));
      }
    } else {
      if (hoveredNodeId !== null) {
        dispatch(setHoveredNode(null));
      }
    }

    setDragging(true);
    setDragPosition({ x: event.clientX, y: event.clientY });
  }, [reactFlowInstance, storeNodes, dispatch, hoveredNodeId]);

  // 그룹(parentId) 자식 노드는 React Flow v12 가 부모 기준 상대 좌표로 자동 이동시킨다.
  // 예전처럼 onNodeDrag 에서 자식 position 을 직접 갱신하면 상대 좌표에 부모 이동량이 또 더해져 2배로 움직인다.
  // ---- 이미 있는 노드를 끌어 그룹(서브플로·반복그룹)에 넣거나 꺼내기 (utils/groupMembership)
  /** 끌기 시작할 때 각 노드의 부모 그룹 경계 — 자동 크기 그룹은 끄는 동안 커지므로 시작 때 것과 비교한다 */
  const dragStartParentRect = useRef(new Map<string, AbsRect>());
  /** 놓으면 들어갈 그룹 — 테두리를 강조한다 */
  const [dropGroupId, setDropGroupId] = useState<string | null>(null);

  const onNodeDragStart = useCallback((_event: React.MouseEvent, _node: Node, nodes?: Node[]) => {
    const m = new Map<string, AbsRect>();
    (nodes && nodes.length ? nodes : [_node]).forEach((n) => {
      if (n.parentId) { const r = absRectOf(n.parentId); if (r) m.set(n.id, r); }
    });
    dragStartParentRect.current = m;
  }, [absRectOf]);

  const onNodeDrag = useCallback((_event: React.MouseEvent, node: Node) => {
    const r = absRectOf(node.id);
    if (!r) return;
    const start = dragStartParentRect.current.get(node.id);
    const stays = !!(node.parentId && start && r.x + r.w / 2 >= start.x && r.x + r.w / 2 <= start.x + start.w
      && r.y + r.h / 2 >= start.y && r.y + r.h / 2 <= start.y + start.h);
    // 꺼내는 중이면 지금 그룹은 후보가 아니다(자동 크기 그룹은 노드를 따라 커진다) — utils/groupMembership 과 같은 규칙
    const exclude = new Set([node.id, ...Array.from(descendantsOf(node.id, storeNodes as any)), ...(node.parentId && !stays ? [node.parentId] : [])]);
    const target = groupAt(r.x + r.w / 2, r.y + r.h / 2, storeNodes as any, absRectOf, exclude);
    const next = stays ? (target ?? node.parentId!) : target;
    const show = next && next !== (node.parentId || null) ? next : null;
    setDropGroupId((cur) => (cur === show ? cur : show));
  }, [absRectOf, storeNodes]);

  // 그룹 노드 생성/업데이트 시 크기 설정 함수
  const getGroupNodeDimensions = (type: string) => {
    return GROUP_NODE_DIMENSIONS[type as keyof typeof GROUP_NODE_DIMENSIONS] || GROUP_NODE_DIMENSIONS.group;
  };

  // onDrop 함수 수정
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setDragging(false);
      setDragPosition(null);

      const currentHoveredNodeId = hoveredNodeId;
      dispatch(setHoveredNode(null));
      dispatch(setHoveredEdge(null));

      if (!reactFlowWrapper.current || !reactFlowInstance) return;

      try {
        const nodeData = event.dataTransfer.getData('application/reactflow-node');
        if (!nodeData) return;

        // 엣지 중간의 + 버튼 근처(반경 26px — WorkflowEdge 의 활성 표시와 같은 기준)에 놓았으면 그 엣지 사이에 삽입.
        // elementFromPoint 는 이웃 노드에 가려지면 실패하므로, 화면상의 + 중심까지 거리로 판정한다.
        const PLUS_DROP_RADIUS = 26;
        const nearest = Array.from(document.querySelectorAll('.react-flow__edge'))
          .map((e) => {
            const r = e.querySelector('rect');
            if (!r) return null;
            const b = r.getBoundingClientRect();
            return { id: e.getAttribute('data-id') || '', d: Math.hypot(b.x + b.width / 2 - event.clientX, b.y + b.height / 2 - event.clientY) };
          })
          .filter((c): c is { id: string; d: number } => !!c && !!c.id)
          .sort((a, b) => a.d - b.d)[0];
        if (nearest && nearest.d <= PLUS_DROP_RADIUS) {
          const { type: dropType } = JSON.parse(nodeData);
          if (dropType && insertNodeOfTypeRef.current) {
            insertNodeOfTypeRef.current(nearest.id, dropType, { x: event.clientX, y: event.clientY });
            return;
          }
        }

        const parsedData = JSON.parse(nodeData);
        const { type, source, id: draggedId, dataDefinition } = parsedData;
        if (!type) return;
        // 오른쪽 '데이터 목록' 에서 드래그한 데이터 정의 → SAM파일/DB테이블 노드 (버튼 '워크플로에 추가' 와 같은 매핑)
        const data = dataDefinition
          ? (() => { const n = nodeFromDataDefinition(dataDefinition, getDefaultNodeData(type).config); return { label: n.label, config: n.config }; })()
          : parsedData.data;

        const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
        const viewport = reactFlowInstance.getViewport();

        // 드롭 위치 계산
        // 툴박스는 노드 모양 드래그 이미지를 **가운데**로 잡는다(utils/dragGhost) — 보이던 그 자리에 놓이게 왼쪽 위로 옮긴다
        const centered = (() => { try { return !!JSON.parse(nodeData)?.[GHOST_CENTERED]; } catch { return false; } })();
        const ghostZoom = reactFlowInstance.getViewport().zoom;
        const dropPosition = {
          x: event.clientX - (centered ? (NODE_W / 2) * ghostZoom : 0),
          y: event.clientY - (centered ? (NODE_H / 2) * ghostZoom : 0),
        };

        // 호버된 노드가 있는 경우 (그룹 노드에 드롭)
        if (currentHoveredNodeId) {
          const targetNode = storeNodes.find(n => n.id === currentHoveredNodeId);
          if (!targetNode) return;

          if (isGroupNodeType(targetNode.type || '')) {
            const dimensions = getGroupNodeDimensions(targetNode.type || 'group');
            const contentRect = document.querySelector(`[data-content-for="${targetNode.id}"]`)?.getBoundingClientRect();
            
            if (!contentRect) return;

            // 드롭 위치를 컨텐츠 영역 기준 상대 좌표로 변환
            const relativeX = (dropPosition.x - contentRect.left) / viewport.zoom;
            const relativeY = (dropPosition.y - contentRect.top) / viewport.zoom;

            // 패딩과 경계를 고려한 위치 조정
            const clampedX = Math.max(10, Math.min(relativeX, dimensions.width - 160));
            const clampedY = Math.max(10, Math.min(relativeY, dimensions.height - 90));
            // 같은 그룹 안의 형제 노드와 겹치지 않는 자리로 (그룹 콘텐츠 영역 안에서 탐색)
            const { x, y } = findFreePosition(
              storeNodes.filter(n => n.parentId === targetNode.id),
              { x: clampedX, y: clampedY },
              { width: 105, height: 56 },
              { bounds: { x: 10, y: 10, width: Math.max(160, dimensions.width - 20), height: Math.max(90, dimensions.height - 20) } },
            );

            const newNode: WorkflowNode = {
              id: getId(),
              type,
              position: { x, y },
              data: { ...(data || getDefaultNodeData(type)) },
              parentId: targetNode.id,
              extent: 'parent',
              draggable: true,
              selected: false,
              expandParent: true,
              className: 'workflow-child-node',
              style: { 
                zIndex: 10,
                width: 150,
                height: 80,
                minWidth: 150,
                minHeight: 80
              }
            };

            dispatch(addNode(newNode));
            return; // 그룹 자식으로 추가했으면 끝 — 아래 최상위 생성 경로로 흘러가 노드가 2개 생기던 버그
          }
        }

        // 빈 영역에 드롭하는 경우
        // 최상위 드롭 위치 — React Flow 공식 변환 (예전 수식은 줌≠1 에서 드롭 지점과 어긋났음)
        const dropFlowPosition = reactFlowInstance.screenToFlowPosition({ x: dropPosition.x, y: dropPosition.y });
        // 다른 최상위 노드와 겹치지 않는 가장 가까운 빈 자리로 (그룹 노드는 큰 컨테이너라 장애물에서 제외 — 그 위에 놓이면 자식이 되는 경로가 따로 있다)
        const flowPosition = findFreePosition(
          storeNodes.filter(n => !n.parentId && !isGroupNodeType(n.type || '')),
          dropFlowPosition,
          nodeSize({ type } as any),
        );

        const newNode: WorkflowNode = {
          id: getId(),
          type,
          position: flowPosition,
          data: { ...(data || getDefaultNodeData(type)) },
          draggable: true,
          className: 'workflow-child-node',  // CSS 클래스로 변경
          style: {
            zIndex: 10,
          }
        };

        if (isGroupNodeType(type)) {
          let width = 600, height = 480;
          
          if (type === 'subflow') {
            width = 640;
            height = 480;
          } else if (type === 'repeatGroup') {
            width = 560;
            height = 440;
          }
          
          newNode.width = width;
          newNode.height = height;
          newNode.style = { width, height };
        }

        if (type === 'compare') {
          // 데이터비교는 결과 SAM파일(레포트) 노드를 함께 만들어 잇는다 — 비교결과가 그리로 나간다(사용자 정의 바).
          // 한 묶음(addFragment)이라 되돌리기 한 번에 둘 다 사라진다
          const base = getDefaultNodeData('csvData') as any;
          const reportId = getId();
          const reportPos = findFreePosition(
            storeNodes.filter(n => !n.parentId && !isGroupNodeType(n.type || '')).concat([newNode as any]),
            { x: flowPosition.x + NODE_W + 80, y: flowPosition.y },
            nodeSize({ type: 'csvData' } as any),
          );
          dispatch(addFragment({
            nodes: [newNode as any, {
              id: reportId, type: 'csvData', position: reportPos, draggable: true, className: 'workflow-child-node', style: { zIndex: 10 },
              data: { ...base, label: t('workflow:compare.reportNodeLabel'),
                config: { ...(base.config || {}), filepath: `reports/compare_${newNode.id}.csv`, delimiter: ',', hasHeader: true, encoding: 'UTF-8', mode: 'WRITE' } },
            } as any],
            edges: [{ id: `e${newNode.id}-${reportId}`, source: newNode.id, target: reportId, type: 'workflow' } as any],
          }));
          return;
        }
        dispatch(addNode(newNode));

      } catch (err) {
        console.error('드롭 처리 오류:', err);
      }
    },
    [reactFlowInstance, dispatch, storeNodes, hoveredNodeId]
  );

  const fitView = useCallback(() => {
    if (reactFlowInstance) {
      reactFlowInstance.fitView({ padding: 0.2 });
    }
  }, [reactFlowInstance]);

  // useAutoLayout(storeNodes, storeEdges, dispatch); // 자동 레이아웃 로직 임시 주석 처리

  useEffect(() => {
    const workflowNodes = storeNodes.filter((n) => n.type === 'workflow');
    const existingPlaceholderIds = new Set(storeNodes.filter(n => n.type === 'placeholder').map(n => n.id));
    const nodesToAddPlaceholder = workflowNodes.filter((n) => {
      const hasRealChild = storeEdges.some(
        (e) =>
          e.source === n.id &&
          storeNodes.find((node) => node.id === e.target && node.type !== 'placeholder')
      );
      const hasPlaceholderChild = storeEdges.some(
        (e) =>
          e.source === n.id &&
          storeNodes.find((node) => node.id === e.target && node.type === 'placeholder')
      );
      return !hasPlaceholderChild && !hasRealChild;
    });
    
    if (nodesToAddPlaceholder.length === 0) return;
    
    // 각 플레이스홀더 노드와 엣지를 개별적으로 추가
    nodesToAddPlaceholder.forEach((n) => {
      const placeholderId = `placeholder-${n.id}-${uuidv4()}`;
      if (!existingPlaceholderIds.has(placeholderId)) {
        // 플레이스홀더 노드 추가
        const placeholderNode = {
          id: placeholderId,
          type: 'placeholder',
          position: {
            x: n.position.x + 200,
            y: n.position.y,
          },
          data: { label: 'placeholder' },   // 화면에 보이지 않는 자리표시 노드
        };
        
        // 연결 엣지 추가
        const placeholderEdge = {
          id: `${n.id}-${placeholderId}`,
          source: n.id,
          target: placeholderId,
          type: 'workflow',
        };
        
        // 노드와 엣지 디스패치
        dispatch(addNode(placeholderNode));
        dispatch(addEdge(placeholderEdge));
      }
    });
  }, [storeNodes, storeEdges, dispatch]);

  // 부모-자식 관계 설정을 위한 헬퍼 함수
  const updateNodeParent = useCallback((nodeId: string, parentId: string, childPosition: { x: number, y: number }) => {
    // 노드 찾기
    const node = storeNodes.find(n => n.id === nodeId);
    if (!node) return;
    
    // 새 속성으로 노드 업데이트
    const updatedNode = {
      ...node,
      position: childPosition,
      parentId: parentId,
      extent: 'parent' as const,
      expandParent: true,
      className: 'workflow-child-node',  // CSS 클래스로 변경
      style: {
        zIndex: 10,  // 부모 노드 위에 렌더링
      }
    };
    
    // 상태 업데이트
    dispatch(updateNode(updatedNode));
    
    // DOM이 업데이트될 시간을 주고 다시 한번 상태 업데이트 (안정성 향상)
    setTimeout(() => {
      // 노드 엘리먼트에 데이터 속성 직접 설정
      const nodeElement = document.querySelector(`[data-id="${nodeId}"]`);
      if (nodeElement) {
        nodeElement.setAttribute('data-parent-id', parentId);
        nodeElement.setAttribute('data-is-child', 'true');
        (nodeElement as HTMLElement).style.zIndex = '10';
      }
      
      // 다시 한번 상태 업데이트
      dispatch(updateNode(updatedNode));
    }, 50);
  }, [storeNodes, dispatch]);
  
  // 부모 자식 관계에 따른 렌더링 순서 보정을 위한 useEffect
  useEffect(() => {
    // 노드 렌더링 순서 관리: 그룹 노드 먼저, 자식 노드 나중에 렌더링되도록 함
    if (!reactFlowInstance) return;
    
    // DOM 노드 요소에 z-index 직접 설정 (상태 업데이트 없이)
    storeNodes.forEach(node => {
      const nodeElement = document.querySelector(`[data-id="${node.id}"]`);
      if (!nodeElement) return;
      
      // 그룹 노드는 낮은 z-index, 자식 노드는 높은 z-index
      const isGroup = isGroupNodeType(node.type || '');
      const hasParent = !!node.parentId;
      
      if (isGroup) {
        (nodeElement as HTMLElement).style.zIndex = '0';
        nodeElement.setAttribute('data-is-group', 'true');
      } else if (hasParent) {
        (nodeElement as HTMLElement).style.zIndex = '0'; // 기존 10에서 0으로 변경
        nodeElement.setAttribute('data-parent-id', node.parentId || '');
        nodeElement.setAttribute('data-is-child', 'true');
      }
    });
  }, [reactFlowInstance, storeNodes]);

  // 노드 삽입 핸들러 수정
  // 엣지 + 클릭 → 삽입할 노드 유형 메뉴 (클릭 지점에 표시)
  type InsertMenu = { kind: 'edge'; edgeId: string; x: number; y: number } | { kind: 'node'; nodeId: string; side: 'left' | 'right' | 'error'; x: number; y: number };
  const [insertMenu, setInsertMenu] = useState<InsertMenu | null>(null);
  const handleInsertNode = useCallback((edgeId: string, pos?: { x: number; y: number }) => {
    setInsertMenu({ kind: 'edge', edgeId, x: pos?.x ?? 0, y: pos?.y ?? 0 });
  }, []);
  // 노드 호버 스텁(+) 클릭 → 그 노드의 왼쪽/오른쪽에 추가할 유형 메뉴
  useEffect(() => {
    const h = (e: Event) => { const d = (e as CustomEvent).detail || {}; if (d.nodeId) setInsertMenu({ kind: 'node', nodeId: d.nodeId, side: d.side === 'left' ? 'left' : d.side === 'error' ? 'error' : 'right', x: d.x ?? 0, y: d.y ?? 0 }); };
    window.addEventListener('openflow:node-insert', h);
    return () => window.removeEventListener('openflow:node-insert', h);
  }, []);
  // 분기 노드의 전용 + → 종류·개수·합류 지점을 한 번에 정하는 창
  const [branchAddFor, setBranchAddFor] = useState<string | null>(null);
  useEffect(() => {
    const h = (e: Event) => { const d = (e as CustomEvent).detail || {}; if (d.nodeId) setBranchAddFor(String(d.nodeId)); };
    window.addEventListener('openflow:branch-add', h);
    return () => window.removeEventListener('openflow:branch-add', h);
  }, []);
  useEffect(() => {
    if (!insertMenu) return;
    const close = (e: Event) => { if (!(e.target as HTMLElement | null)?.closest?.('[data-insert-menu]')) setInsertMenu(null); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setInsertMenu(null); };
    // 캡처 단계(true): 캔버스(d3-zoom)·노드가 mousedown 전파를 막아도 바깥 클릭을 놓치지 않는다
    document.addEventListener('mousedown', close, true);
    document.addEventListener('keydown', esc, true);
    return () => { document.removeEventListener('mousedown', close, true); document.removeEventListener('keydown', esc, true); };
  }, [insertMenu]);

  // 선택한 유형의 노드를 엣지 중간에 끼워 넣는다: 원래 엣지 제거 → 새 노드 → source→새 노드→target 엣지 2개
  const insertNodeOfType = useCallback((edgeId: string, type: string, at?: { x: number; y: number }) => {
    setInsertMenu(null);
    const edge = storeEdges.find(e => e.id === edgeId);
    if (!edge) return;
    const sourceNode = storeNodes.find(n => n.id === edge.source);
    const targetNode = storeNodes.find(n => n.id === edge.target);
    if (!sourceNode || !targetNode) return;

    const newNodeId = getId();
    const size = nodeSize({ type } as any);
    // 새 노드는 + 아이콘(클릭 지점) 자리에 — 좌우는 클릭 지점 중심, 상하는 두 노드의 행에 맞춘다
    const anchor = at ?? (insertMenu ? { x: insertMenu.x, y: insertMenu.y } : null);
    const clickFlow = reactFlowInstance && anchor
      ? reactFlowInstance.screenToFlowPosition(anchor)
      : { x: (sourceNode.position.x + targetNode.position.x) / 2, y: (sourceNode.position.y + targetNode.position.y) / 2 };
    const rowY = (sourceNode.position.y + targetNode.position.y) / 2;
    const GAP = 40;
    // 두 노드 사이 간격이 새 노드 폭보다 좁으면 + 위치에 중심을 맞춘 채로는 source 와 겹치므로,
    // source 오른쪽 끝 + 간격보다 왼쪽으로는 가지 않게 보정한다 (하류는 그만큼 더 밀린다)
    const minX = sourceNode.position.x + nodeSize(sourceNode).width + GAP;
    const position = { x: Math.max(clickFlow.x - size.width / 2, minX), y: rowY };

    // target 과 그 하류(엣지를 따라 도달하는 모든 노드)를 새 노드 폭 + 간격만큼 오른쪽으로 민다
    const shift = size.width + GAP;
    const downstream = new Set<string>();
    const queue = [edge.target];
    while (queue.length) {
      const id = queue.shift()!;
      if (downstream.has(id)) continue;
      downstream.add(id);
      storeEdges.forEach(e => { if (e.source === id && !downstream.has(e.target)) queue.push(e.target); });
    }
    downstream.delete(edge.source); // 순환이 있어도 source 는 밀지 않는다
    // 새 노드의 오른쪽 끝이 target 의 (밀린 뒤) 왼쪽보다 앞서지 않도록 필요한 만큼만 추가 보정
    const overlapExtra = Math.max(0, position.x + size.width + GAP - (targetNode.position.x + shift));
    downstream.forEach(id => {
      const n = storeNodes.find(x => x.id === id);
      if (n) dispatch(updateNodePosition({ id, position: { x: n.position.x + shift + overlapExtra, y: n.position.y } }));
    });

    const newNode: WorkflowNode = {
      id: newNodeId,
      type,
      position,
      data: { ...getDefaultNodeData(type) },
      draggable: true,
      className: 'workflow-child-node',
      style: { zIndex: 10 },
    } as WorkflowNode;

    dispatch(removeEdge(edgeId));
    dispatch(addNode(newNode));
    dispatch(addEdge({ id: `${edge.source}-${newNodeId}`, source: edge.source, target: newNodeId, type: 'workflow' }));
    dispatch(addEdge({ id: `${newNodeId}-${edge.target}`, source: newNodeId, target: edge.target, type: 'workflow' }));
    dispatch(setSelectedNode(newNodeId));

    setTimeout(() => {
      if (reactFlowInstance && autoZoomRef.current) {
        reactFlowInstance.fitView({ padding: 0.25, includeHiddenNodes: false, minZoom: 0.4, maxZoom: 1.5 });
      }
    }, 100);
  }, [storeEdges, storeNodes, dispatch, reactFlowInstance, insertMenu]);
  // onDrop(앞에서 정의됨)이 참조할 수 있도록 ref 로 노출
  insertNodeOfTypeRef.current = insertNodeOfType;

  /** 노드의 왼쪽/오른쪽에 새 노드를 만들고 엣지로 잇는다 (왼쪽이면 새 노드 → 기준 노드, 오른쪽이면 기준 노드 → 새 노드) */
  const insertNodeBeside = useCallback((nodeId: string, side: 'left' | 'right' | 'error', type: string) => {
    setInsertMenu(null);
    const base = storeNodes.find(n => n.id === nodeId);
    if (!base) return;
    // 양쪽 모두 같은 간격 — 선 가운데의 + 와 노드 아래 이름이 들어갈 만큼 띄운다.
    // 예전에는 오른쪽 40px, 왼쪽은 새 노드 너비를 옛 기본값(205)으로 잡아 140px 이 되어 양쪽이 달랐다(현장 2026-10-01)
    const GAP = 120;
    const typeSize = nodeSize({ type } as any);
    const size = isGroupNodeType(type) || type === 'memo' ? typeSize : { width: NODE_W, height: NODE_H };
    const baseSize = nodeSize(base);
    // 예외 흐름은 정상 흐름 아래로 — 30° 로 내려가는 선이 지날 자리를 둔다 (utils/errorFlow)
    const errOff = errorTargetOffset(baseSize.width);
    const desired = side === 'error'
      ? { x: base.position.x + errOff.dx, y: base.position.y + errOff.dy }
      : side === 'right'
        ? { x: base.position.x + baseSize.width + GAP, y: base.position.y }
        : { x: base.position.x - GAP - size.width, y: base.position.y };
    // 같은 좌표계(같은 부모)의 노드와 겹치지 않는 자리
    const siblings = storeNodes.filter(n => (n.parentId || null) === (base.parentId || null));
    const position = findFreePosition(siblings, desired, size);
    const newNodeId = getId();
    const newNode: WorkflowNode = {
      id: newNodeId, type, position,
      data: { ...getDefaultNodeData(type) },
      draggable: true, className: 'workflow-child-node', style: { zIndex: 10 },
      ...(base.parentId ? { parentId: base.parentId, extent: 'parent' as const } : {}),
    } as WorkflowNode;
    dispatch(addNode(newNode));
    if (side === 'error') dispatch(addEdge({ id: `${nodeId}-${newNodeId}`, source: nodeId, target: newNodeId, sourceHandle: ERROR_HANDLE_ID, type: 'workflow' } as any));
    else if (side === 'right') dispatch(addEdge({ id: `${nodeId}-${newNodeId}`, source: nodeId, target: newNodeId, type: 'workflow' }));
    else dispatch(addEdge({ id: `${newNodeId}-${nodeId}`, source: newNodeId, target: nodeId, type: 'workflow' }));
    dispatch(setSelectedNode(newNodeId));
    setTimeout(() => { if (reactFlowInstance && autoZoomRef.current) reactFlowInstance.fitView({ padding: 0.25, includeHiddenNodes: false, minZoom: 0.4, maxZoom: 1.5 }); }, 100);
  }, [storeNodes, dispatch, reactFlowInstance]);

  /**
   * 분기 노드에 후행 노드를 한꺼번에 더한다 (개수·합류 지점까지).
   *
   * 자리는 branchChildPositions 가 정한다 — **위에서 아래로 하나씩**. 한 번에 여러 개를 더하든 나중에
   * 하나씩 더하든 같은 규칙이라, 이미 달린 후행 아래로 이어 붙는다.
   */
  const addBranchTargets = useCallback((branchId: string, r: BranchAddResult) => {
    setBranchAddFor(null);
    const branch = storeNodes.find(n => n.id === branchId);
    if (!branch) return;
    const size = nodeSize({ type: r.type } as any);
    const existing = storeNodes.filter(n => storeEdges.some(e => e.source === branchId && e.target === n.id));
    const positions = branchChildPositions(branch, existing, r.count, size);

    const newIds: string[] = [];
    positions.forEach((position) => {
      const id = getId();
      newIds.push(id);
      dispatch(addNode({
        id, type: r.type, position,
        data: { ...getDefaultNodeData(r.type) },
        draggable: true, className: 'workflow-child-node', style: { zIndex: 10 },
        ...(branch.parentId ? { parentId: branch.parentId, extent: 'parent' as const } : {}),
      } as WorkflowNode));
      dispatch(addEdge({ id: `${branchId}-${id}`, source: branchId, target: id, type: 'workflow' }));
    });

    // 합류 지점 — 고른 것이 있으면 새 노드들을 그리로 잇는다
    let mergeId: string | null = r.merge === MERGE_NONE ? null : r.merge;
    if (r.merge === MERGE_NEW) {
      mergeId = getId();
      const mSize = nodeSize({ type: 'merge' } as any);
      // 새 노드들의 오른쪽, 세로로는 그 가운데
      const top = Math.min(...positions.map(p => p.y));
      const bottom = Math.max(...positions.map(p => p.y + size.height));
      const desired = { x: positions[0].x + size.width + BRANCH_COL_GAP, y: (top + bottom) / 2 - mSize.height / 2 };
      const siblings = storeNodes.filter(n => (n.parentId || null) === (branch.parentId || null));
      dispatch(addNode({
        id: mergeId, type: 'merge',
        position: findFreePosition(siblings as any, desired, mSize),
        data: { ...getDefaultNodeData('merge') },
        draggable: true, className: 'workflow-child-node', style: { zIndex: 10 },
        ...(branch.parentId ? { parentId: branch.parentId, extent: 'parent' as const } : {}),
      } as WorkflowNode));
    }
    if (mergeId) newIds.forEach(id => dispatch(addEdge({ id: `${id}-${mergeId}`, source: id, target: mergeId as string, type: 'workflow' })));

    dispatch(setSelectedNode(newIds[0] || branchId));
    setTimeout(() => { if (reactFlowInstance && autoZoomRef.current) reactFlowInstance.fitView({ padding: 0.25, includeHiddenNodes: false, minZoom: 0.4, maxZoom: 1.5 }); }, 100);
  }, [storeNodes, storeEdges, dispatch, reactFlowInstance]);

  /** 삽입 메뉴의 이웃 노드 유형 — DFD 규칙으로 선택 가능/불가를 가른다 */
  const insertNeighborTypes = useMemo((): string[] => {
    if (!insertMenu) return [];
    if (insertMenu.kind === 'edge') {
      const e = storeEdges.find(x => x.id === insertMenu.edgeId);
      return e ? [storeNodes.find(n => n.id === e.source)?.type, storeNodes.find(n => n.id === e.target)?.type].filter((t): t is string => !!t) : [];
    }
    const n = storeNodes.find(x => x.id === insertMenu.nodeId);
    return n?.type ? [n.type] : [];
  }, [insertMenu, storeEdges, storeNodes]);

  /** 지금 접혀 있는 그룹 — 자동 생성 그룹(분할처리)은 data.collapsed 가 false 로 꺼져 있지 않으면 접힘이다 */
  const collapsedGroupIds = useMemo(() => new Set(
    storeNodes.filter((n: any) => isGroupNodeType(n.type || '') && n.data?.readOnly && n.data?.collapsed !== false).map((n: any) => n.id),
  ), [storeNodes]);

  /** 접고 펼치는 동안만 이동·크기 변화를 부드럽게 (index.css 의 .of-collapse-anim) */
  const [collapseAnim, setCollapseAnim] = useState(false);

  // 노드 위 ▸/▾ — 접힘 상태는 노드에 남겨 두어 워크플로를 다시 열어도 그대로다.
  // 펼치면 그룹이 넓어지므로 **오른쪽 노드들을 그만큼 밀고**, 접으면 도로 당긴다 — 겹쳐 보이지 않게.
  useEffect(() => {
    const h = (e: Event) => {
      const id = (e as CustomEvent).detail?.nodeId;
      const node: any = storeNodes.find((n: any) => n.id === id);
      if (!node) return;
      const nowCollapsed = node.data?.collapsed !== false;   // 지금 접혀 있나
      // 좌표는 건드리지 않는다 — 접힘일 때 오른쪽 노드를 당겨 그리는 일은 flowNodes 가 한다.
      // 여기서 또 옮기면 접었다 펴기를 되풀이할 때마다 배치가 조금씩 밀린다.
      setCollapseAnim(true);
      dispatch(updateNodeData({ id, data: { collapsed: !nowCollapsed } } as any));
      window.setTimeout(() => setCollapseAnim(false), 320);
    };
    window.addEventListener('openflow:toggle-group-collapse', h);
    return () => window.removeEventListener('openflow:toggle-group-collapse', h);
  }, [storeNodes, dispatch]);

  // 그룹(서브플로·반복그룹) 안의 노드는 경계에 가두지도(extent), 끄는 대로 그룹을 넓히지도(expandParent) 않는다.
  // 밖으로 끌면 그룹이 따라 커져서 노드가 "딸려 오는" 것처럼 보였다(현장 2026-10-12) — 이제 그룹은 그대로 있고
  // 노드만 나간다. 시작할 때의 경계 밖에 놓으면 그룹에서 빠진다(onNodeDragStop → planMembership).
  // 저장된 노드의 extent·expandParent 값은 건드리지 않는다 — 화면에 넘길 때만 푼다.
  const flowNodes = useMemo(() => {
    const sized = storeNodes.some(n => n.parentId && (n.extent || (n as any).expandParent))
      ? storeNodes.map(n => (n.parentId && (n.extent || (n as any).expandParent)) ? { ...n, extent: undefined, expandParent: false } : n)
      : storeNodes;
    // 탐색기에서 고른 노드는 캔버스에서도 **선택된 것으로 보여야** 한다 — 가운데로 옮겨 주기만 하면
    // 어느 것을 골랐는지 화면에서 알 수 없다. 캔버스에서 직접 고른 선택(node.selected)은 그대로 둔다.
    const base = !selectedNodeId || sized.some(n => n.id === selectedNodeId && n.selected)
      ? sized
      : sized.map(n => (n.selected ? { ...n, selected: false } : n.id === selectedNodeId ? { ...n, selected: true } : n));

    // 끌고 있는 노드를 놓으면 들어갈 그룹 — 테두리를 강조한다(index.css .of-drop-target)
    // 그룹 안 노드에는 of-in-group — 마우스를 올려도 노드 층을 연결선 위로 올리지 않는다(index.css).
    // 올리면 그룹 상자의 배경까지 함께 올라가 안쪽 연결선을 덮었다(현장 2026-10-01)
    const marked = base.map(n => {
      const cls = [n.id === dropGroupId ? 'of-drop-target' : '', n.parentId ? 'of-in-group' : ''].filter(Boolean).join(' ');
      return cls ? { ...n, className: `${n.className || ''} ${cls}`.trim() } : n;
    });
    // 접힌 그룹(분할처리 등)은 한 노드처럼 보여 준다 — 자식은 숨기고 그룹 상자만 작게.
    // **저장된 캔버스는 그대로 둔다**: 접힘은 보기 상태일 뿐이고, DAG 는 안쪽 노드로 만들어져야 한다.
    if (collapsedGroupIds.size === 0) return marked;

    // 접힌 그룹이 차지하던 폭만큼 **오른쪽 노드를 당겨 그린다** — 그러지 않으면 펼친 배치 그대로라
    // 접어도 연결선만 길게 남는다. 저장된 좌표는 그대로 두므로 펼치면 제자리로 돌아온다.
    const shrink = marked
      .filter(n => collapsedGroupIds.has(n.id))
      .map(n => ({ x: n.position.x, delta: Math.max(0, Number((n.style as any)?.width ?? (n as any).width ?? 105) - 105) }))
      .sort((a, b) => a.x - b.x);
    const shiftFor = (x: number) => shrink.reduce((sum, g) => (x >= g.x + 105 ? sum + g.delta : sum), 0);

    return marked
      .filter(n => !(n.parentId && collapsedGroupIds.has(n.parentId)))
      .map(n => {
        if (!n.parentId && !collapsedGroupIds.has(n.id)) {
          const dx = shiftFor(n.position.x);
          return dx ? { ...n, position: { x: n.position.x - dx, y: n.position.y } } : n;
        }
        if (!collapsedGroupIds.has(n.id)) return n;
        // 접으면 상자가 작아지므로, 펼쳤을 때 차지하던 영역의 **세로 가운데**에 놓는다 —
        // 좌상단에 그대로 두면 소스·타깃과 높이가 어긋나 연결선이 크게 돌아간다. (저장값은 그대로)
        const h = Number((n.style as any)?.height ?? (n as any).height ?? 56);
        const dy = Math.max(0, (h - 56) / 2);
        return {
          ...n,
          position: { x: n.position.x, y: n.position.y + dy },
          style: { ...(n.style || {}), width: 105, height: 56 },
          width: 105, height: 56,
        };
      });
  }, [storeNodes, collapsedGroupIds, selectedNodeId, dropGroupId]);

  // 엣지에 스타일 적용
  // DFD 규칙 위반에 걸린 엣지 → 캔버스에서 주황 점선으로 표시
  const dfdViolationEdges = useMemo(() => {
    const m = new Map<string, string>();
    checkDfd(storeNodes, storeEdges).forEach(v => v.edgeIds.forEach(eid => m.set(eid, v.message)));
    return m;
  }, [storeNodes, storeEdges]);

  /** 접힌 그룹을 한 노드처럼 잇는다 — 안쪽끼리의 연결은 감추고, 드나드는 연결은 그룹에 붙인다 */
  const visibleEdges = useMemo(() => {
    if (collapsedGroupIds.size === 0) return storeEdges;
    const ownerOf = (id: string) => {
      const n: any = storeNodes.find((x: any) => x.id === id);
      if (!n) return id;
      if (collapsedGroupIds.has(n.id)) return n.id;
      return n.parentId && collapsedGroupIds.has(n.parentId) ? n.parentId : id;
    };
    const seen = new Set<string>();
    const out: any[] = [];
    for (const e of storeEdges as any[]) {
      const s = ownerOf(e.source);
      const t = ownerOf(e.target);
      if (s === t) continue;                      // 접힌 그룹 안쪽끼리 — 보이지 않는다
      const key = `${s}->${t}`;
      if (seen.has(key)) continue;                // 파티션이 여러 개라 같은 짝이 여러 번 나온다
      seen.add(key);
      out.push(s === e.source && t === e.target ? e : { ...e, id: `${e.id}__collapsed`, source: s, target: t, sourceHandle: undefined, targetHandle: undefined });
    }
    return out;
  }, [storeEdges, storeNodes, collapsedGroupIds]);

  const edgesWithStyles = useMemo(() => {
    const NO_INSERT_TYPES = new Set(['branch', 'merge', 'quality', 'compare']);
    const typeOf = (id: string) => storeNodes.find((n: any) => n.id === id)?.type;
    return visibleEdges.map(edge => {
      const noInsert = NO_INSERT_TYPES.has(typeOf(edge.source) || '') || NO_INSERT_TYPES.has(typeOf(edge.target) || '');
      // 엣지가 호버된 상태인지 확인
      const isHovered = edge.id === hoveredEdgeId;
      
      // 호버된 엣지에 스타일 적용
      return {
        ...edge,
        selected: selectedEdgeIds.has(edge.id),
        // 예외 흐름(실패 시 가는 길)은 빨간 점선 — 값이 흐르는 길과 눈으로 구분된다(DFD 위반은 주황 점선이라 색으로 갈린다)
        // 데이터비교로 들어가는 선은 점선 — 데이터가 흐르는 길이 아니라 「이 둘을 본다」 는 뜻(사용자 정의, 2026-10-20)
        style: isHovered ? { stroke: '#ff0072', strokeWidth: 4, ...(typeOf(edge.target) === 'compare' ? { strokeDasharray: '5 4' } : {}) }
          : isErrorEdge(edge) ? { stroke: '#dc2626', strokeWidth: 2, strokeDasharray: '6 4' }
          : typeOf(edge.target) === 'compare' ? { stroke: '#7c3aed', strokeWidth: 1.5, strokeDasharray: '5 4' } : undefined,
        data: { 
          ...edge.data, 
          dfdViolation: dfdViolationEdges.get(edge.id) || null,
          noInsert,
          onInsertNode: (pos?: { x: number; y: number }) => handleInsertNode(edge.id, pos),
          // 툴박스에서 드래그 중이면 + 를 크게 강조하고, 커서가 가까우면 활성 표시 (WorkflowEdge 가 판정)
          toolboxDragging: dragging,
          dragPosition: dragging ? dragPosition : null,
        }
      };
    });
  }, [visibleEdges, storeNodes, hoveredEdgeId, handleInsertNode, dragging, dragPosition, dfdViolationEdges, selectedEdgeIds]);

  // 워크플로 변경 시 프로젝트 상태 업데이트를 위한 useEffect 추가
  // 빈 캔버스는 **사용자가 편집해서 비운 것일 때만** 보관한다(되돌리기 기록이 있다 = 이 워크플로를 열고 편집했다).
  // 여는 도중의 잠깐 빈 상태를 보관하면 안 되고, 다 지운 것을 보관하지 않으면 갔다 올 때 마지막에 지운 것이 되살아났다.
  const editedHere = useSelector((state: any) => (state.workflow.history?.past?.length || 0) > 0);
  useEffect(() => {
    const empty = storeNodes.length === 0 && storeEdges.length === 0;
    if (currentWorkflow && currentWorkflow.id && (!empty || editedHere)) {
      console.log('워크플로 데이터 자동 저장:', currentWorkflow.id, storeNodes.length, '노드,', storeEdges.length, '엣지');
      
      // 프로젝트 상태에 워크플로 데이터 저장
      dispatch(updateWorkflowData({
        id: currentWorkflow.id,
        nodes: storeNodes,
        edges: storeEdges,
        settings: wfSettings,
        userEmptied: empty,
      } as any));
    }
  }, [currentWorkflow, storeNodes, storeEdges, wfSettings, editedHere, dispatch]);

  // centerNodeId가 변경될 때마다 해당 노드로 화면 중심 이동 (탐색기에서 노드 클릭)
  useEffect(() => {
    if (reactFlowInstance && centerNodeId) {
      const nodeToCenter = storeNodes.find(node => node.id === centerNodeId);
      if (nodeToCenter) {
        // position 은 노드의 좌상단이므로 크기의 절반을 더해 실제 중앙을 맞춘다.
        const size = nodeSize(nodeToCenter as any);
        // zoom 을 생략하면 React Flow 가 maxZoom(2배)로 확 당겨 버린다 — 현재 배율을 기준으로 적당히만 확대.
        const zoom = Math.min(1.5, Math.max(reactFlowInstance.getZoom(), 1));
        reactFlowInstance.setCenter(
          nodeToCenter.position.x + size.width / 2,
          nodeToCenter.position.y + size.height / 2,
          { duration: 500, zoom },
        );

        // 상태 초기화
        setTimeout(() => {
          dispatch(clearCenterNode());
        }, 100);
      }
    }
  }, [centerNodeId, reactFlowInstance, storeNodes, dispatch]);

  // 팝업에서 확인 시에만 대체 실행
  const handleReplaceConfirm = () => {
    if (replaceTargetNode && replaceNewType && replaceNewData) {
      const updatedNode = {
        ...replaceTargetNode,
        type: replaceNewType,
        data: {
          ...replaceNewData,
          workflowId: replaceTargetNode.data?.workflowId
        },
        draggable: true,
        className: 'workflow-child-node',  // CSS 클래스로 변경
      };
      dispatch(updateNode(updatedNode));
    }
    setReplaceDialogOpen(false);
    setReplaceTargetNode(null);
    setReplaceNewType(null);
    setReplaceNewData(null);
  };

  // Rename the second handleInsertNode function to handleReplaceAdd
  const handleReplaceAdd = () => {
    if (replaceNewType && replaceNewData && replaceTargetNode) {
      const newNode: WorkflowNode = {
        id: getId(),
        type: replaceNewType,
        position: { ...replaceTargetNode.position },
        data: {
          ...replaceNewData,
        },
        draggable: true,
        className: 'workflow-child-node',  // CSS 클래스로 변경
      };
      dispatch(addNode(newNode));
    }
    setReplaceDialogOpen(false);
    setReplaceTargetNode(null);
    setReplaceNewType(null);
    setReplaceNewData(null);
  };

  const handleReplaceCancel = () => {
    setReplaceDialogOpen(false);
    setReplaceTargetNode(null);
    setReplaceNewType(null);
    setReplaceNewData(null);
  };

  // Move the utility function inside the component to access reactFlowInstance
  const updateChildrenDragState = (parentId: string, isDragging: boolean) => {
    if (!reactFlowInstance) return;
    
    // 모든 자식 노드 요소 찾기
    const childElements = document.querySelectorAll(`[data-parent-id="${parentId}"]`);
    
    // 자식 노드에 드래그 상태 속성 설정
    childElements.forEach(el => {
      el.setAttribute('data-parent-dragging', isDragging ? 'true' : 'false');
    });
    
    console.log(`자식 노드 상태 업데이트: 부모=${parentId}, 드래그=${isDragging}, 자식 수=${childElements.length}`);
  };

  // Find the handleDeleteEdge function (around line 1060) and modify it to handle the event
  const handleDeleteEdge = useCallback((edgeId: string) => {
    // Original implementation
    console.log('Edge delete 호출:', edgeId);
    dispatch(removeEdge(edgeId));
  }, [dispatch]);

  // Create a new function that accepts an event and calls handleDeleteEdge with the correct parameter
  const handleDeleteEdgeClick = useCallback((edgeId: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    handleDeleteEdge(edgeId);
  }, [handleDeleteEdge]);

  /**
   * 부모-자식 노드 관계 시각화 업데이트 함수
   */
  const updateNodeParentVisualizations = useCallback(() => {
    if (!reactFlowInstance) return;
    
    const allNodes = reactFlowInstance.getNodes();
    
    // 그룹 노드 먼저 처리
    allNodes.forEach(node => {
      if (isGroupNodeType(node.type || '')) {
        const groupElement = document.querySelector(`[data-id="${node.id}"]`) as HTMLElement;
        if (groupElement) {
          groupElement.classList.add('workflow-group-node');
          
          const contentElement = groupElement.querySelector('.group-content') as HTMLElement;
          if (contentElement) {
            contentElement.classList.add('workflow-group-content');
            contentElement.style.setProperty('overflow', 'hidden', 'important');
          }
        }
      }
    });
    
    // 자식 노드 처리
    allNodes.forEach(node => {
      if (node.parentId) {
        const nodeElement = document.querySelector(`[data-id="${node.id}"]`) as HTMLElement;
        if (nodeElement) {
          nodeElement.classList.add('workflow-child-node');
          // 위치는 React Flow 가 계산한다 (자식 = 부모 절대좌표 + 상대좌표).
          // 예전처럼 여기서 transform 을 !important 로 덮어쓰면 자식이 엉뚱한 곳에 그려지고
          // 드래그 중 React Flow 의 갱신까지 막혀 노드가 튀었다. 부모 영역 제한은 노드의 extent:'parent' 가 담당.
        }
      }
    });
  }, [reactFlowInstance]);

  /**
   * 노드 드래그 종료 시 부모-자식 관계 업데이트 핸들러
   */
  const onNodeDragStop = useCallback(
    (_: React.MouseEvent<Element, MouseEvent>, node: Node, draggedNodes?: Node[]) => {
      const groupNodeTypes = ['group', 'subflow', 'repeatGroup'];
      const isGroupNode = groupNodeTypes.includes(node.type || 'default');
      setDropGroupId(null);

      // 그룹에 넣기·꺼내기 — 소속이 바뀌는 노드가 있으면 그것만 하고 끝낸다(겹침 밀어내기는 새 그룹 안에서 다시 할 일이 아니다)
      const ids = (draggedNodes && draggedNodes.length ? draggedNodes : [node]).map((n) => n.id);
      const changes = planMembership(ids, storeNodes as any, absRectOf, dragStartParentRect.current);
      dragStartParentRect.current = new Map();
      if (changes.size > 0) {
        const updated = storeNodes.map((n: any) => {
          const c = changes.get(n.id);
          if (!c) return n;
          const { parentId, extent, expandParent, ...rest } = n;
          return c.parentId
            ? { ...rest, position: c.position, parentId: c.parentId, extent: 'parent', expandParent: true }
            : { ...rest, position: c.position };
        });
        // 끌기(위치)와 한 번에 되돌아가도록 앞 기록에 합친다 (historyMiddleware meta.historyMerge)
        dispatch({ ...replaceCanvas({ nodes: parentsFirst(updated) as any, edges: storeEdges as any }), meta: { historyMerge: true } } as any);
        return;
      }

      // 놓은 자리가 다른 노드와 겹치면 같은 좌표계(같은 parentId)의 노드들 사이 가장 가까운 빈 자리로 밀어낸다.
      // 그룹 노드끼리는 겹침 판정에서 제외 (컨테이너를 겹쳐 두는 배치도 허용).
      const siblings = storeNodes.filter(n => n.id !== node.id && (n.parentId || null) === (node.parentId || null) && !isGroupNodeType(n.type || ''));
      if (!isGroupNode && siblings.length > 0) {
        const free = findFreePosition(siblings, node.position, nodeSize(node), { excludeId: node.id });
        if (free.x !== node.position.x || free.y !== node.position.y) {
          dispatch(updateNodePosition({ id: node.id, position: free }));
        }
      }
      
      if (isGroupNode) {
        dispatch(updateNode({
          ...node,
          data: {
            ...node.data,
            isDragging: false
          }
        }));
      }
    },
    [dispatch, storeNodes, storeEdges, absRectOf]
  );

  useEffect(() => {
    updateNodeParentVisualizations();
  }, [updateNodeParentVisualizations, storeNodes]);

  // CSS 스타일 수정
  const workflowStyles = `
  .workflow-group-node {
    position: relative !important;
    transform-origin: 0 0 !important;
    box-sizing: border-box !important;
  }

  .workflow-group-content {
    position: relative !important;
    overflow: hidden !important;
    width: 100% !important;
    height: calc(100% - 30px) !important;
    min-height: 100px !important;
    box-sizing: border-box !important;
    padding: 10px !important;
  }

  .workflow-child-node {
    position: absolute !important;
    transform-origin: 0 0 !important;
    pointer-events: all !important;
  }

  .react-flow__node.workflow-child-node {
    position: absolute !important;
    transform-origin: 0 0 !important;
  }
  `;

  // useEffect에서 스타일 추가
  useEffect(() => {
    // 스타일 태그가 이미 존재하는지 확인
    const existingStyle = document.getElementById('workflow-styles');
    if (!existingStyle) {
      const styleElement = document.createElement('style');
      styleElement.id = 'workflow-styles';
      styleElement.textContent = workflowStyles;
      document.head.appendChild(styleElement);
    }
    
    return () => {
      // 컴포넌트 언마운트 시 스타일 제거
      const styleElement = document.getElementById('workflow-styles');
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  return (
    <div className="w-full h-full flex flex-col">
      <DfdCheckBar nodes={storeNodes} edges={storeEdges} />
      <svg style={{ position: 'absolute', width: 0, height: 0, top: 0, left: 0, zIndex: 9999 }}>
        <defs>
          <marker
            id="react-flow__arrowclosed"
            markerWidth="15"
            markerHeight="15"
            viewBox="-10 -10 20 20"
            orient="auto"
            refX="0"
            refY="0"
          >
            <polygon 
              fill="#555"
              stroke="#555"
              strokeWidth="1"
              points="-5,-5 2,0 -5,5 -5,-5"
            />
          </marker>
          <marker
            id="react-flow__arrow"
            markerWidth="15"
            markerHeight="15"
            viewBox="-10 -10 20 20"
            orient="auto"
            refX="0"
            refY="0"
          >
            <polyline
              fill="none"
              stroke="#555"
              strokeWidth="1"
              points="-5,-5 2,0 -5,5"
            />
          </marker>
        </defs>
      </svg>
      <div 
        className={`w-full flex-1 relative${collapseAnim ? ' of-collapse-anim' : ''}`}
        ref={reactFlowWrapper}
        style={{ 
          height: 'calc(100% - 60px)',
          position: 'relative',
          overflow: 'hidden'
        }}
        onWheelCapture={noteZoomGesture}
        onTouchStartCapture={noteZoomGesture}
        onDragStart={(e) => {
          // 노드 내부에서 시작된 드래그가 아닌 경우에만 실행
          const target = e.target as HTMLElement;
          if (!target.closest('.react-flow__node')) {
            e.preventDefault();
          }
        }}
      >
        
        {/* 엣지 + 클릭 시 삽입할 노드 유형 메뉴 */}
        {insertMenu && createPortal(
          <div
            data-insert-menu
            className="fixed z-[10000] w-56 max-h-[440px] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-sm"
            style={{ left: Math.min(insertMenu.x + 8, window.innerWidth - 240), top: Math.max(8, Math.min(insertMenu.y + 8, window.innerHeight - 452)) }}
            role="menu"
          >
            <div className="flex items-center justify-between px-3 py-1.5 text-xs font-semibold text-gray-500 border-b border-gray-200">
              <span>{insertMenu.kind === 'edge' ? t('workflow:insert.between') : insertMenu.side === 'left' ? t('workflow:insert.left') : t('workflow:insert.right')}</span>
              <button type="button" className="px-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100" title={t('workflow:insert.closeEsc')} onClick={() => setInsertMenu(null)}>✕</button>
            </div>
            {(['data', 'process', 'control', 'exec'] as const).map(cat => (
              <div key={cat}>
                <div className="px-3 pt-2 pb-1 text-2xs uppercase tracking-wide text-gray-400">{categoryLabelOf(cat)}</div>
                {INSERTABLE_NODE_TYPES.filter(e => e.category === cat).map(e => {
                  const blocked = insertBlockedReason(e.type, insertNeighborTypes);
                  return (
                    <button
                      key={e.type}
                      type="button"
                      role="menuitem"
                      disabled={!!blocked}
                      title={blocked || undefined}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left ${blocked ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'}`}
                      onClick={() => { if (blocked) return; if (insertMenu.kind === 'edge') insertNodeOfType(insertMenu.edgeId, e.type); else insertNodeBeside(insertMenu.nodeId, insertMenu.side, e.type); }}
                    >
                      <span>{typeLabelOf(e.type)}</span>
                      {/* 사용 보류 — 막지는 않지만 새로 쓰지 말라는 표시 (nodeCatalog.ON_HOLD_NODE_TYPES) */}
                      {!blocked && isOnHoldType(e.type) && <span className="ml-auto text-2xs text-amber-600">{t('workflow:toolbox.onHold')}</span>}
                      {blocked && <span className="ml-auto text-2xs text-gray-300">{t('workflow:insert.blocked')}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>,
          document.body
        )}

        {/* 분기 + → 후행 종류·개수·합류 지점을 한 번에 정하는 창 */}
        {branchAddFor && (() => {
          const branch = storeNodes.find(n => n.id === branchAddFor);
          if (!branch) return null;
          const merges = storeNodes.filter(n => n.type === 'merge')
            .map(n => ({ id: n.id, label: String((n.data as any)?.label || typeLabelOf('merge')) }));
          return (
            <BranchAddDialog
              branchLabel={String((branch.data as any)?.label || typeLabelOf('branch'))}
              merges={merges}
              onAdd={(r) => addBranchTargets(branch.id, r)}
              onClose={() => setBranchAddFor(null)}
            />
          );
        })()}

        {/* 드래그 중 위치 표시(파란 원+십자)는 두지 않는다 — 툴박스는 놓일 노드 모양 자체를 끌어 보여 준다(utils/dragGhost).
            드래그 위치(dragPosition)는 연결선 + 에 놓아 끼우는 판정에 계속 쓴다 */}
        
        <ReactFlow
          key="workflow-canvas-flow"
          // 여럿을 고른 동안에는 노드마다의 아이콘(이력·삭제)과 + 를 감춘다 — 위에 뜨는 선택 도구 막대가 그 일을 한다
          className={selectedIds.length > 1 ? 'of-multiselect' : undefined}
          nodes={flowNodes}
          edges={edgesWithStyles}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onReconnect={onReconnect}
          reconnectRadius={14}
          onNodeClick={onNodeClick}
          onNodeContextMenu={onNodeContextMenu}
          onInit={onInit as any}
          onMoveEnd={onMoveEnd}
          onDrop={onDrop}
          onDragOver={onDragOver}
          data-dev-mode={process.env.NODE_ENV === 'development' ? 'true' : 'false'}
          onNodeDragStart={onNodeDragStart}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          nodeTypes={memoizedNodeTypes}
          edgeTypes={memoizedEdgeTypes}
          minZoom={0.4}
          maxZoom={2}
          fitView={false}
          defaultEdgeOptions={defaultEdgeOptions}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          nodesDraggable={true}
          elementsSelectable={true}
          selectNodesOnDrag={false}
          panOnScroll={true}
          // 빈 곳 끌기 = 화면 이동(왼쪽·가운데·오른쪽 버튼 모두). 범위 선택은 Shift 를 누른 채 끌기(selectionKeyCode 기본값)
          // — 2026-10-01 왼쪽 끌기를 범위 선택으로 바꿨다가 화면 이동이 안 된다는 지적으로 되돌렸다
          selectionOnDrag={false}
          selectionKeyCode="Shift"
          panOnDrag={[0, 1, 2]}
          onPaneContextMenu={(e) => e.preventDefault()}
          onSelectionEnd={() => {
            // 끝난 직후의 선택을 읽는다 — 선택 반영(onNodesChange → 스토어)이 같은 틱에 끝나지 않을 수 있다
            window.setTimeout(() => setBoxIds((appStore.getState().workflow.nodes as any[]).filter((n) => n.selected).map((n) => n.id)), 0);
          }}
          proOptions={{ 
            hideAttribution: true,
            account: 'paid',
          }}
          elevateNodesOnSelect={false}
          elevateEdgesOnSelect={true}
          snapToGrid={true}
          snapGrid={[10, 10]}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden'
          }}
          deleteKeyCode={['Backspace', 'Delete']}
        >
          {/* 점 색상은 index.css 의 --xy-background-pattern-dots-color (라이트/다크) 가 결정 */}
          <Background gap={16} />
          {/* 워크플로명 워터마크 — 뷰포트 변환 밖(ReactFlow 직계 자식)이라 이동·확대에도 제자리에 고정된다 (datamodeler 와 같은 방식).
              오른쪽 위는 도구 모음(Save~실행·파일)이 차지하므로 왼쪽 위에 두고, 길면 말줄임한다 */}
          {(currentWorkflow?.name || projectName) && (
            <div
              className="workflow-watermark pointer-events-none select-none text-2xl font-bold"
              style={{ position: 'absolute', top: 22, left: 20, maxWidth: '40%', overflow: 'hidden', textOverflow: 'ellipsis', zIndex: 0, opacity: 0.5, letterSpacing: '0.05em', whiteSpace: 'nowrap' }}
            >
              {projectName ? `${projectName} / ${currentWorkflow?.name || ''}` : (currentWorkflow?.name || '')}
            </div>
          )}
          <Controls onZoomIn={markManualZoom} onZoomOut={markManualZoom}>
            <ControlButton
              onClick={() => setAutoZoom(v => !v)}
              title={autoZoom ? t('workflow:canvas.zoomAuto') : t('workflow:canvas.zoomManual')}
              aria-label={t('workflow:canvas.zoomToggle')}
              className={autoZoom ? 'zoom-mode-auto' : 'zoom-mode-manual'}
            >
              {/* @ts-ignore react-icons IconType 과 React 18 타입 불일치 회피 */}
              {autoZoom ? <FiMaximize /> : <FiLock />}
            </ControlButton>
          </Controls>
          <MiniMap />
          <SelectionToolbar nodes={storeNodes as any} edges={storeEdges as any} selectedIds={selectedIds} boxSelected={boxSelected} workflowName={currentWorkflow?.name} />
        </ReactFlow>
        {overlay}
        {runMenu && <NodeRunMenu menu={runMenu} onClose={closeRunMenu} />}

        {/* 팀장 요청으로 디버그 정보 오버레이 주석 처리 (2026-08-28). 되살리려면 이 주석 블록을 해제하면 됨.
        {process.env.NODE_ENV === 'development' && (
          <div
            className="absolute top-0 right-0 bg-black bg-opacity-70 text-white p-3 rounded-bl-md"
            style={{ zIndex: 999, maxWidth: '350px', fontSize: '12px' }}
          >
            <div className="font-bold mb-1">디버그 정보</div>
            <div>
              <div>호버된 노드: {hoveredNodeId || '없음'}</div>
              <div>호버된 엣지: {hoveredEdgeId || '없음'}</div>
              <div className="mt-1">총 노드 수: {storeNodes.length}</div>
              <div>총 엣지 수: {storeEdges.length}</div>

              <div className="mt-2 p-1 bg-purple-800 rounded">
                <div className="font-bold">줌 정보:</div>
                <div>현재 줌: {reactFlowInstance?.getViewport().zoom.toFixed(2) || '정보 없음'}</div>
                <div>감지 반경: {(100 / (reactFlowInstance?.getViewport().zoom || 1)).toFixed(0)}px</div>
              </div>

              {hoveredNodeId && (
                <div className="mt-2 p-1 bg-blue-800 rounded">
                  <div className="font-bold">호버 노드 정보:</div>
                  {(() => {
                    const node = storeNodes.find(n => n.id === hoveredNodeId);
                    if (!node) return '노드 정보 없음';
                    return (
                      <>
                        <div>ID: {node.id}</div>
                        <div>타입: {node.type}</div>
                        <div>위치: {JSON.stringify(node.position)}</div>
                        <div>크기: {node.width || '?'} x {node.height || '?'}</div>
                      </>
                    );
                  })()}
                </div>
              )}
              {hoveredEdgeId && (
                <div className="mt-2 p-1 bg-green-800 rounded">
                  <div className="font-bold">호버 엣지 정보:</div>
                  {(() => {
                    const edge = storeEdges.find(e => e.id === hoveredEdgeId);
                    if (!edge) return '엣지 정보 없음';
                    return (
                      <>
                        <div>ID: {edge.id}</div>
                        <div>Source: {edge.source}</div>
                        <div>Target: {edge.target}</div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
        */}
      </div>
      <div className="mt-4 p-4 bg-white shadow rounded">
        {/* ... existing code ... */}
      </div>
      
      {replaceDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">{t('workflow:canvas.replaceTitle')}</h3>
            <div className="mb-6">{t('workflow:canvas.replaceBody')}</div>
            <div className="flex justify-end gap-2">
              <button className="px-4 py-2 bg-gray-200 rounded" onClick={handleReplaceCancel}>{t('workflow:canvas.cancel')}</button>
              <button className="px-4 py-2 bg-green-600 text-white rounded" onClick={handleReplaceAdd}>{t('workflow:canvas.add')}</button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded" onClick={handleReplaceConfirm}>{t('workflow:canvas.confirm')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 컴포넌트 자체를 메모이제이션
export default React.memo(WorkflowCanvas);